import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { formatCurrency, formatNumber, formatPercentage } from '../../utils/formatters';

// Portal component for rendering dropdowns outside the parent DOM hierarchy
const Portal = ({ children }) => {
  return ReactDOM.createPortal(children, document.body);
};

// Column filter component
const ColumnFilterDropdown = React.forwardRef(({ 
  column, 
  uniqueValues, 
  selectedFilters, 
  onFilterChange, 
  onSortAsc, 
  onSortDesc,
  onClose,
  position = {} 
}, ref) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredValues = uniqueValues.filter(value => 
    value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const dropdown = (
    <div 
      ref={ref}
      className="fixed z-[9999] bg-white rounded-lg shadow-lg border border-gray-200 min-w-[240px] divide-y divide-gray-100"
      style={{
        top: position.top || 0,
        left: position.left || 0,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3">
        <span className="text-sm font-medium text-gray-700">Filter {column.charAt(0).toUpperCase() + column.slice(1)}</span>
        <button 
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-50 rounded-full"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Sort Buttons */}
      <div className="p-2">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onSortAsc}
            className="flex items-center justify-center px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded transition-colors border border-gray-200"
          >
            <svg className="w-3 h-3 mr-1" viewBox="0 0 16 16" fill="none" stroke="currentColor">
              <path d="M2 4h8M2 8h6M2 12h4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13 12V4M13 4l-2 2M13 4l2 2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Sort A → Z
          </button>
          <button
            onClick={onSortDesc}
            className="flex items-center justify-center px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded transition-colors border border-gray-200"
          >
            <svg className="w-3 h-3 mr-1" viewBox="0 0 16 16" fill="none" stroke="currentColor">
              <path d="M2 4h8M2 8h6M2 12h4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13 4v8M13 12l-2-2M13 12l2-2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Sort Z → A
          </button>
        </div>
      </div>

      {/* Search Box */}
      <div className="p-2">
        <div className="relative">
          <svg className="w-3.5 h-3.5 absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-8 pr-2.5 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
          />
        </div>
      </div>

      {/* Checkboxes */}
      <div className="p-2 max-h-60 overflow-y-auto">
        <label className="flex items-center px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer group transition-colors">
          <div className="relative flex items-center">
            <input
              type="checkbox"
              checked={selectedFilters.length === uniqueValues.length}
              onChange={(e) => {
                if (e.target.checked) {
                  onFilterChange(uniqueValues);
                } else {
                  onFilterChange([]);
                }
              }}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-offset-0"
            />
            <span className="ml-2 text-sm font-medium text-gray-700 group-hover:text-gray-900">Select All</span>
          </div>
          <span className="ml-auto text-xs text-gray-400 group-hover:text-gray-600">
            {selectedFilters.length}/{uniqueValues.length}
          </span>
        </label>
        
        <div className="mt-1 space-y-1">
          {filteredValues.map((value) => (
            <label key={value} className="flex items-center px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer group transition-colors">
              <div className="relative flex items-center">
                <input
                  type="checkbox"
                  checked={selectedFilters.includes(value)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onFilterChange([...selectedFilters, value]);
                    } else {
                      onFilterChange(selectedFilters.filter(v => v !== value));
                    }
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-offset-0"
                />
                <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900">{value}</span>
              </div>
            </label>
          ))}
          
          {filteredValues.length === 0 && (
            <div className="px-2 py-3 text-center text-sm text-gray-500">
              No matches found
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return <Portal>{dropdown}</Portal>;
});

const DataTable = ({ 
  data, 
  activeTab, 
  selectedCompany, 
  timePeriod, 
  onTimePeriodChange,
  // Filter props passed from parent
  sortOrder,
  selectedMetrics,
  selectedCompanies,
  selectedGroups,
  // Callback to notify parent about detailed view state
  onDetailViewChange
}) => {
  const [collapsedTables, setCollapsedTables] = useState(new Set());
  const [collapsedCompanies, setCollapsedCompanies] = useState(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [collapsedProducts, setCollapsedProducts] = useState(new Set()); // Track collapsed products in detail view
  const [detailView, setDetailView] = useState(null); // { type: 'parent' | 'child', groupId: string, groupName: string }
  const [detailSelectedMetrics, setDetailSelectedMetrics] = useState(new Set(['totalSales', 'unitsSold', 'tacos', 'conversion', 'organicSales'])); // Default selected metrics
  const [parentFilter, setParentFilter] = useState(null); // Track which parent's children to show in child view
  const [activeDropdown, setActiveDropdown] = useState(null); // { type: 'stat' | 'column', data: {...}, position: { top, left } }
  const [detailSortConfig, setDetailSortConfig] = useState({ key: null, direction: 'asc' }); // { key: column, direction: 'asc' | 'desc' }
  const [columnFilters, setColumnFilters] = useState({
    brand: [],
    product: [],
    custom: []
  });
  const [pinnedStat, setPinnedStat] = useState(null); // Store single pinned stat name
  const [statOrder, setStatOrder] = useState([]); // Store custom order of all stats
  const [hoveredStat, setHoveredStat] = useState(null); // Store currently hovered stat
  const [draggedStat, setDraggedStat] = useState(null); // Store currently dragged stat
  const [dragOverStat, setDragOverStat] = useState(null); // Store stat being dragged over
  const [dragPosition, setDragPosition] = useState(null); // Store position for drop indicator ('above' | 'below')
  const activeDropdownRef = useRef(null);
  
  // Refs for auto-scrolling to latest week/month
  const parentCompanyTableRef = useRef(null);
  const companiesTableRef = useRef(null);
  const groupsTableRef = useRef(null);
  const detailViewTableRef = useRef(null);

  // Add main table state management for drag and pinning
  const [mainPinnedStat, setMainPinnedStat] = useState(null); // Store single pinned stat name for main tables
  const [mainStatOrder, setMainStatOrder] = useState([]); // Store custom order for main tables
  const [mainHoveredStat, setMainHoveredStat] = useState(null); // Store currently hovered stat for main tables
  const [mainDraggedStat, setMainDraggedStat] = useState(null); // Store currently dragged stat for main tables
  const [mainDragOverStat, setMainDragOverStat] = useState(null); // Store stat being dragged over for main tables
  const [mainDragPosition, setMainDragPosition] = useState(null); // Store position for drop indicator for main tables
  const [parentWeekSort, setParentWeekSort] = useState({ column: null, direction: 'desc' }); // Store week column sorting for parent table
  const [companiesWeekSort, setCompaniesWeekSort] = useState({ column: null, direction: 'desc' }); // Store week column sorting for companies table
  const [groupsWeekSort, setGroupsWeekSort] = useState({ column: null, direction: 'desc' }); // Store week column sorting for groups table

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdownRef.current && !activeDropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    if (activeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  // Initialize default stat order when detail view changes
  useEffect(() => {
    if (detailView && statOrder.length === 0) {
      const statsToUse = detailView.type === 'parent' ? parentStats : childStats;
      const defaultOrder = ['Total Sales', ...statsToUse.filter(s => s.label !== 'Total Sales').map(s => s.label).sort()];
      setStatOrder(defaultOrder);
    }
  }, [detailView, statOrder.length]);

  // Initialize default stat order for main tables
  useEffect(() => {
    if (mainStatOrder.length === 0) {
      const mainMetrics = getFilteredMetrics();
      const defaultOrder = ['Total Sales', ...mainMetrics.filter(m => m.label !== 'Total Sales').map(m => m.label).sort()];
      setMainStatOrder(defaultOrder);
    }
  }, [mainStatOrder.length, selectedMetrics]);

  // Auto-scroll to latest week/month when data loads or changes
  useEffect(() => {
    const scrollToLatestPeriod = (tableRef) => {
      if (!tableRef || !tableRef.current) return;
      
      const maxPeriod = getMaxPeriodWithData();
      const periodLabel = timePeriod === 'monthly' ? 'month' : 'week';
      
      // Find the column header for the latest period with data
      const table = tableRef.current.querySelector('table');
      if (!table) return;
      
      const headers = table.querySelectorAll('th');
      let targetHeader = null;
      
      // Look for the header that matches the latest period
      headers.forEach((header) => {
        const text = header.textContent?.toLowerCase() || '';
        const periodMatch = text.match(new RegExp(`${periodLabel}\\s*(\\d+)`));
        if (periodMatch) {
          const periodNum = parseInt(periodMatch[1]);
          if (periodNum === maxPeriod) {
            targetHeader = header;
          }
        }
      });
      
      if (targetHeader) {
        // Scroll the target column into view, with some offset to show context
        const containerWidth = tableRef.current.clientWidth;
        const scrollLeft = targetHeader.offsetLeft - (containerWidth / 3);
        
        tableRef.current.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: 'smooth'
        });
      }
    };
    
    // Small delay to ensure DOM is ready
    const timeout = setTimeout(() => {
      scrollToLatestPeriod(parentCompanyTableRef);
      scrollToLatestPeriod(companiesTableRef);
      scrollToLatestPeriod(groupsTableRef);
      scrollToLatestPeriod(detailViewTableRef);
    }, 300);
    
    return () => clearTimeout(timeout);
  }, [data, timePeriod, detailView]);

  // Available metrics for filtering
  const availableMetrics = [
        { id: 'all', label: 'All Metrics' },
        { id: 'totalSales', label: 'Total Sales' },
        { id: 'unitsSold', label: 'Units Sold' },
        { id: 'aov', label: 'AOV' },
        { id: 'adSpend', label: 'Ad Spend' },
        { id: 'tacos', label: 'Tacos' },
        { id: 'profitPercent', label: 'Profit %' },
        { id: 'profitTotal', label: 'Profit Total' }
      ];

  // Helper function to generate sample values for demonstration
  const generateSampleValue = (type) => {
    switch (type) {
      case 'currency':
        return Math.floor(Math.random() * 50000) + 5000; // $5,000 - $55,000
      case 'percentage':
        return Math.floor(Math.random() * 30) + 5; // 5% - 35%
      case 'number':
        return Math.floor(Math.random() * 2000) + 100; // 100 - 2,100
      default:
        return Math.floor(Math.random() * 1000) + 10;
    }
  };

  // Helper function to format values
  const formatValue = (value, type) => {
    // Show "-" for null, undefined, or empty values, but show 0 for actual zero values
    if (value === undefined || value === null || value === '') return '-';
    
    switch (type) {
      case 'currency':
        return formatCurrency(value);
      case 'percentage':
        return formatPercentage(value);
      case 'number':
        return formatNumber(value);
      default:
        return value;
    }
  };

  // Get metric color (updated for detailed view)
  const getMetricColor = (metricName, value) => {
    if (!value || value === '-') return 'text-secondary-500';
    
    // Convert percentage values for comparison
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[,%$]/g, '')) : value;
    
    // All numbers now display in black
    return 'text-black font-medium';
  };

  const toggleTable = (tableId) => {
    setCollapsedTables(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tableId)) {
        newSet.delete(tableId);
      } else {
        newSet.add(tableId);
      }
      return newSet;
    });
  };

  const toggleCompany = (companyId) => {
    setCollapsedCompanies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(companyId)) {
        newSet.delete(companyId);
      } else {
        newSet.add(companyId);
      }
      return newSet;
    });
  };

  const toggleGroup = (groupId) => {
    setCollapsedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const toggleProduct = (productKey) => {
    setCollapsedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productKey)) {
        newSet.delete(productKey);
      } else {
        newSet.add(productKey);
      }
      return newSet;
    });
  };

  // Filter metrics based on selection
  const shouldShowMetric = (metricKey) => {
    if (selectedMetrics?.includes('all')) return true;
    return selectedMetrics?.includes(metricKey);
  };

  // Check if company should be shown
  const shouldShowCompany = (companyId) => {
    if (selectedCompanies?.includes('all')) return true;
    return selectedCompanies?.includes(companyId);
  };

  // Check if group should be shown
  const shouldShowGroup = (groupId) => {
    if (selectedGroups?.includes('all')) return true;
    return selectedGroups?.includes(groupId);
  };



  // Filter metrics based on selection
  const getFilteredMetrics = () => {
    const baseMetrics = [
      { key: 'totalSales', label: 'Total Sales', type: 'currency' },
      { key: 'unitsSold', label: 'Units Sold', type: 'number' },
      { key: 'aov', label: 'AOV', type: 'currency' },
      { key: 'adSpend', label: 'Ad Spend', type: 'currency' },
      { key: 'tacos', label: 'Tacos', type: 'percentage' },
      { key: 'profitPercent', label: 'Profit %', type: 'percentage' },
      { key: 'profitTotal', label: 'Profit Total', type: 'currency' }
    ];

    if (selectedMetrics?.includes('all')) {
      return baseMetrics;
    }

    return baseMetrics.filter(metric => selectedMetrics?.includes(metric.key));
  };

  const renderTableHeader = (title, icon, tableId) => (
    <div className="bg-gradient-to-r from-secondary-50 to-secondary-100 border-b border-secondary-200 dark:from-slate-800 dark:to-slate-700 dark:border-slate-600">
      <div 
        className="px-6 py-4 cursor-pointer hover:bg-gradient-to-r hover:from-secondary-100 hover:to-secondary-150 transition-colors duration-200 dark:hover:from-slate-700 dark:hover:to-slate-600"
        onClick={() => toggleTable(tableId)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
              <span className="text-white text-sm">{icon}</span>
        </div>
            <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-slate-100">{title}</h3>
              {/* Show pinned stat indicator for main tables */}
              {tableId !== 'compare' && (
                <div className="flex items-center space-x-2 mt-1">
                  {/* Show week sort indicator for tables with week sorting */}
                  {((tableId === 'parent-company' && parentWeekSort.column) || 
                    (tableId === 'companies' && companiesWeekSort.column) || 
                    (tableId === 'groups' && groupsWeekSort.column)) ? (
                    <div className="flex items-center space-x-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zM10 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1V4zM17 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1V4zM10 10a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-3zM17 10a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-3z"/>
                      </svg>
                      <span className="font-medium">
                        {tableId === 'parent-company' ? 'Week Filter: ' : 
                         tableId === 'companies' ? 'Companies sorted by: ' :
                         tableId === 'groups' ? 'Groups sorted by: ' : ''}{(
                          tableId === 'parent-company' ? parentWeekSort.column :
                          tableId === 'companies' ? companiesWeekSort.column :
                          tableId === 'groups' ? groupsWeekSort.column : ''
                        ).replace('week', 'Week ')} {tableId === 'parent-company' ? 'highlighted' : ''}
                        {mainPinnedStat && (tableId === 'companies' || tableId === 'groups') && ` (${mainPinnedStat})`}
                        {(tableId === 'companies' || tableId === 'groups') && (
                          (tableId === 'companies' ? companiesWeekSort.direction :
                           tableId === 'groups' ? groupsWeekSort.direction : 'desc'
                          ) === 'asc' ? ' (Low to High)' : ' (High to Low)'
                        )}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (tableId === 'parent-company') {
                            setParentWeekSort({ column: null, direction: 'desc' });
                          } else if (tableId === 'companies') {
                            setCompaniesWeekSort({ column: null, direction: 'desc' });
                          } else if (tableId === 'groups') {
                            setGroupsWeekSort({ column: null, direction: 'desc' });
                          }
                        }}
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                        title={tableId === 'companies' ? "Clear company sorting" : 
                               tableId === 'groups' ? "Clear group sorting" : "Clear week filter"}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : mainPinnedStat ? (
                    <div className="flex items-center space-x-2 px-2 py-1 bg-primary-100 text-primary-800 rounded-full text-xs">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 4l-8 8h6v8h4v-8h6z" />
                      </svg>
                      <span className="font-medium">Pinned: {mainPinnedStat}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMainPinnedStat(null);
                        }}
                        className="text-primary-600 hover:text-primary-800 transition-colors"
                        title="Unpin stat"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2 px-2 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                        <path d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z" />
                      </svg>
                      <span className="font-medium">{
                        tableId === 'compare' ? 'Year over Year Comparison' : 
                        tableId === 'companies' ? 'Click week columns to sort companies • Drag & Drop Enabled' :
                        tableId === 'groups' ? 'Click week columns to sort groups • Drag & Drop Enabled' :
                        'Click week columns to filter • Drag & Drop Enabled'
                      }</span>
                    </div>
                  )}
                </div>
              )}
            </div>
      </div>
          <div 
            className="text-primary-700 hover:text-primary-900 transition-colors duration-200"
            aria-label={collapsedTables.has(tableId) ? "Expand" : "Collapse"}
          >
            <svg 
              className={`w-6 h-6 transform transition-transform duration-200 ${collapsedTables.has(tableId) ? '' : 'rotate-180'}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>
      </div>
    );

  const getColumns = () => {
    // Show only Sept 7-13, 2025 data (which is stored in week1)
    const columns = [
      { key: 'name', label: 'METRIC', width: 'w-48', align: 'left', hasIndicator: true },
      { 
        key: 'week1', 
        label: 'SEPT 7-13, 2025', 
        width: 'w-32', 
        align: 'center', 
        hasIndicator: false 
      }
    ];
    
    return columns;
  };

  // Helper function to find the maximum week/month that has data
  const getMaxPeriodWithData = () => {
    const periodLabel = timePeriod === 'monthly' ? 'month' : 'week';
    let maxPeriod = timePeriod === 'weekly' ? 0 : 1; // Default to start index
    
    // Check parent company data
    const parentCompanyData = data.parentCompanies?.['jungle-group'];
    if (parentCompanyData) {
      Object.values(parentCompanyData).forEach(metricData => {
        if (metricData && typeof metricData === 'object') {
          Object.keys(metricData).forEach(key => {
            if (key.startsWith(periodLabel)) {
              const periodNum = parseInt(key.replace(periodLabel, ''));
              if (!isNaN(periodNum) && metricData[key] != null && metricData[key] !== 0) {
                maxPeriod = Math.max(maxPeriod, periodNum);
              }
            }
          });
        }
      });
    }
    
    // Check company accounts data
    const companyAccounts = data.companyAccounts?.['jungle-group'];
    if (companyAccounts) {
      Object.values(companyAccounts).forEach(company => {
        if (company.data) {
          Object.values(company.data).forEach(metricData => {
            if (metricData && typeof metricData === 'object') {
              Object.keys(metricData).forEach(key => {
                if (key.startsWith(periodLabel)) {
                  const periodNum = parseInt(key.replace(periodLabel, ''));
                  if (!isNaN(periodNum) && metricData[key] != null && metricData[key] !== 0) {
                    maxPeriod = Math.max(maxPeriod, periodNum);
                  }
                }
              });
            }
          });
        }
        // Check groups
        if (company.groups) {
          company.groups.forEach(group => {
            if (group.data) {
              Object.values(group.data).forEach(metricData => {
                if (metricData && typeof metricData === 'object') {
                  Object.keys(metricData).forEach(key => {
                    if (key.startsWith(periodLabel)) {
                      const periodNum = parseInt(key.replace(periodLabel, ''));
                      if (!isNaN(periodNum) && metricData[key] != null && metricData[key] !== 0) {
                        maxPeriod = Math.max(maxPeriod, periodNum);
                      }
                    }
                  });
                }
              });
            }
          });
        }
      });
    }
    
    return maxPeriod;
  };

  const renderTable = (tableData, tableId, isCollapsed, weekSortState = null, weekSortHandler = null) => {
    const columns = getColumns();
    
    if (isCollapsed) return null;

    // Check table type and get appropriate sort state
    const isParentCompanyTable = tableId === 'parent-company';
    const isCompaniesTable = tableId === 'companies';
    const isGroupsTable = tableId === 'groups';
    
    // Get the appropriate week sort state
    const currentWeekSort = weekSortState || (
      isParentCompanyTable ? parentWeekSort :
      isCompaniesTable ? companiesWeekSort :
      isGroupsTable ? groupsWeekSort :
      { column: null, direction: 'desc' }
    );
    
    // Get the appropriate week sort handler
    const currentWeekSortHandler = weekSortHandler || (
      isParentCompanyTable ? handleParentWeekSort :
      isCompaniesTable ? handleCompaniesWeekSort :
      isGroupsTable ? handleGroupsWeekSort :
      null
    );

    return (
      <div className="relative">
        <div ref={parentCompanyTableRef} className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-200px)]">
          <table className="w-full table-fixed">
            <thead className="bg-gradient-to-r from-secondary-50 to-secondary-100 sticky top-0 z-50">
              <tr>
                {columns.map((column, colIndex) => (
                  <th 
                    key={column.key} 
                    className={`py-3 ${column.align === 'center' ? 'text-center' : 'text-left'} text-xs font-semibold text-secondary-700 uppercase tracking-wider ${column.width} border-b border-secondary-200 ${
                      column.key === 'name' ? 'sticky left-0 z-40 bg-gradient-to-r from-secondary-50 to-secondary-100 px-4' : 'bg-gradient-to-r from-secondary-50 to-secondary-100 px-1'
                    } ${currentWeekSortHandler && column.key.startsWith('week') ? 'cursor-pointer hover:bg-secondary-100 transition-colors' : ''}`}
                    style={{
                      ...column.key === 'name' ? { minWidth: '192px', left: '0' } : { minWidth: '80px' },
                      backgroundColor: column.key === 'name' ? '#f8fafc' : undefined
                    }}
                    onClick={currentWeekSortHandler && column.key.startsWith('week') ? () => currentWeekSortHandler(column.key) : undefined}
                    title={currentWeekSortHandler && column.key.startsWith('week') ? 
                      `Click to filter by ${column.key}${mainPinnedStat && (isCompaniesTable || isGroupsTable) ? ` (highlighting ${mainPinnedStat})` : ''}` : undefined}
                  >
                    <div className={`flex items-center ${column.align === 'center' ? 'justify-center' : ''} space-x-1`}>
                      {column.label}
                      {column.hasIndicator && (
                        <div className="w-1.5 h-1.5 bg-primary-400 rounded-full"></div>
                      )}
                      {/* Show sort indicator for week columns */}
                      {currentWeekSortHandler && column.key.startsWith('week') && currentWeekSort.column === column.key && (
                        <svg 
                          className={`w-3 h-3 transform transition-transform duration-200 ${currentWeekSort.direction === 'asc' ? '' : 'rotate-180'}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      )}
                      {/* Show active week sort indicator */}
                      {currentWeekSortHandler && column.key.startsWith('week') && currentWeekSort.column === column.key && (
                        <div className="flex items-center space-x-1">
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                          {/* Show pin indicator when using pinned metric for week sorting */}
                          {mainPinnedStat && (isCompaniesTable || isGroupsTable) && (
                            <svg className="w-2.5 h-2.5 text-primary-600" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 4l-8 8h6v8h4v-8h6z" />
                            </svg>
                          )}
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-100">
              {tableData.map((row, index) => (
                <tr 
                  key={index} 
                  draggable={true}
                  onDragStart={(e) => {
                    setMainDraggedStat(row.name);
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', row.name);
                    e.currentTarget.style.opacity = '0.5';
                  }}
                  onDragOver={(e) => {
                    if (mainDraggedStat && mainDraggedStat !== row.name) {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      
                      const rect = e.currentTarget.getBoundingClientRect();
                      const midY = rect.top + rect.height / 2;
                      const position = e.clientY < midY ? 'above' : 'below';
                      
                      setMainDragOverStat(row.name);
                      setMainDragPosition(position);
                    }
                  }}
                  onDragEnter={(e) => {
                    if (mainDraggedStat && mainDraggedStat !== row.name) {
                      e.preventDefault();
                      
                      const rect = e.currentTarget.getBoundingClientRect();
                      const midY = rect.top + rect.height / 2;
                      const position = e.clientY < midY ? 'above' : 'below';
                      
                      setMainDragOverStat(row.name);
                      setMainDragPosition(position);
                    }
                  }}
                  onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget)) {
                      setMainDragOverStat(null);
                      setMainDragPosition(null);
                    }
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    if (mainDraggedStat && mainDraggedStat !== row.name) {
                      const filteredMetrics = getFilteredMetrics();
                      const currentOrder = mainStatOrder.length > 0 ? [...mainStatOrder] : 
                        ['Total Sales', ...filteredMetrics.filter(m => m.label !== 'Total Sales').map(m => m.label).sort()];
                      
                      const newOrder = currentOrder.filter(stat => stat !== mainDraggedStat);
                      const targetIndex = newOrder.indexOf(row.name);
                      
                      if (targetIndex !== -1) {
                        const insertIndex = mainDragPosition === 'above' ? targetIndex : targetIndex + 1;
                        newOrder.splice(insertIndex, 0, mainDraggedStat);
                      } else {
                        newOrder.push(mainDraggedStat);
                      }
                      
                      setMainStatOrder(newOrder);
                    }
                    setMainDraggedStat(null);
                    setMainDragOverStat(null);
                    setMainDragPosition(null);
                  }}
                  onDragEnd={(e) => {
                    setMainDraggedStat(null);
                    setMainDragOverStat(null);
                    setMainDragPosition(null);
                    e.currentTarget.style.opacity = '';
                  }}
                  className={`
                    group transition-all duration-200 cursor-move
                    ${mainPinnedStat === row.name ? 'bg-primary-50 hover:bg-primary-100' : 'bg-white hover:bg-secondary-50'}
                    ${mainDraggedStat === row.name ? 'opacity-50 bg-primary-50' : ''}
                    ${mainDragOverStat === row.name && mainDragPosition === 'above' ? 'border-t-4 border-blue-500' : ''}
                    ${mainDragOverStat === row.name && mainDragPosition === 'below' ? 'border-b-4 border-blue-500' : ''}
                    ${index % 2 === 0 ? '' : 'bg-secondary-25'}
                  `}
                >
                  {columns.map((column, colIndex) => (
                    <td 
                      key={column.key} 
                      className={`py-2 text-sm whitespace-nowrap ${column.align === 'center' ? 'text-center' : 'text-left'} ${
                        column.key === 'name' ? 'px-4' : 'px-1'
                      } ${currentWeekSortHandler && currentWeekSort.column === column.key ? 'bg-blue-50' : ''}`}
                      style={{
                        ...column.key === 'name' ? { 
                          minWidth: '192px',
                          position: 'sticky',
                          left: 0,
                          zIndex: 1000,
                          boxShadow: '2px 0 4px rgba(0, 0, 0, 0.1)',
                          backgroundColor: mainPinnedStat === row.name ? '#fef3f2' : 
                            (index % 2 === 0 ? '#ffffff' : '#fafafa')
                        } : { 
                          minWidth: '80px',
                          backgroundColor: currentWeekSortHandler && currentWeekSort.column === column.key ? 
                            'rgb(239 246 255)' : // bg-blue-50
                            (index % 2 === 0 ? 'white' : 'var(--color-secondary-25)')
                        }
                      }}
                      onMouseEnter={() => column.key === 'name' && setMainHoveredStat(row.name)}
                      onMouseLeave={() => column.key === 'name' && setMainHoveredStat(null)}
                    >
                      {column.key === 'name' ? (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              if (mainPinnedStat === row.name) {
                                setMainPinnedStat(null);
                              } else {
                                setMainPinnedStat(row.name);
                              }
                            }}
                            className={`
                              ${(mainHoveredStat === row.name || mainPinnedStat === row.name) ? 'opacity-100' : 'opacity-0'}
                              group-hover:opacity-100 transition-opacity duration-200
                              ${mainPinnedStat === row.name ? 'text-primary-600' : 'text-secondary-400 hover:text-secondary-600'}
                            `}
                            title={mainPinnedStat === row.name ? 'Unpin stat' : 'Pin stat to top'}
                          >
                            <svg 
                              className={`w-4 h-4 transform transition-transform duration-200 ${mainPinnedStat === row.name ? 'rotate-180' : ''}`} 
                              fill="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path d="M12 4l-8 8h6v8h4v-8h6z" />
                            </svg>
                          </button>
                          <span className="font-medium text-secondary-900">
                            {row[column.key] || '-'}
                          </span>
                        </div>
                      ) : (
                      <span className={`${
                        column.key.includes('week') ? getMetricColor(row.name, row[column.key]) :
                        'text-black'
                      } ${currentWeekSortHandler && currentWeekSort.column === column.key ? 'font-semibold' : ''}`}>
                        {row[column.key] || '-'}
                      </span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Parent Company Data (The Jungle Group, INC)
  const getParentCompanyData = () => {
    const parentCompanyData = data.parentCompanies?.['jungle-group'];
    console.log('📊 getParentCompanyData - parentCompanyData:', parentCompanyData);
    
    if (!parentCompanyData) {
      // Return sample data if no data exists for demonstration
      const filteredMetrics = getFilteredMetrics();
      return filteredMetrics.map(metric => {
        // Generate sample week data with some trend
        const baseValue = generateSampleValue(metric.type);
        const weekValues = {};
        const rawValues = {};
        
        // For weekly, start from 0; for monthly, start from 1
        const startIndex = timePeriod === 'weekly' ? 0 : 1;
        const endIndex = timePeriod === 'weekly' ? 14 : 15; // 15 weeks (0-14) or 15 months (1-15)
        const periodLabel = timePeriod === 'monthly' ? 'month' : 'week';
        
        // Generate sample data for all periods
        for (let i = startIndex; i <= endIndex; i++) {
          // Add some variance to make the data interesting for sorting
          const variance = (Math.random() - 0.5) * 0.3; // ±15% variance
          const weekValue = Math.max(0, Math.floor(baseValue * (1 + variance)));
          weekValues[`${periodLabel}${i}`] = formatValue(weekValue, metric.type);
          rawValues[`${periodLabel}${i}`] = weekValue;
        }
        
        return {
          name: metric.label,
          metricKey: metric.key,
          metricType: metric.type,
          ...weekValues,
          rawValues
        };
      });
    }

    const filteredMetrics = getFilteredMetrics();
    console.log('📊 DataTable - parentCompanyData.data:', parentCompanyData.data);
    console.log('📊 DataTable - Available metrics:', Object.keys(parentCompanyData.data || {}));

    // Map metrics to rows with only week1 (Sept 7-13, 2025) data
    const rows = filteredMetrics.map(metric => {
      const metricData = parentCompanyData.data?.[metric.key] || {};
      
      const row = {
        name: metric.label,
        metricKey: metric.key,
        metricType: metric.type,
        week1: formatValue(metricData.week1, metric.type),
        rawValues: {
          week1: metricData.week1
        }
      };
      
      return row;
    });

    // Sort rows with pinned stats always at the top, maintain metric order regardless of week filtering
    return rows.sort((a, b) => {
      // ALWAYS check pinned stat first - pinned stats go to the top regardless of other sorting
      if (mainPinnedStat) {
        const aIsPinned = a.name === mainPinnedStat;
        const bIsPinned = b.name === mainPinnedStat;
        
        if (aIsPinned && !bIsPinned) return -1; // Pinned item goes to top
        if (!aIsPinned && bIsPinned) return 1;  // Pinned item goes to top
        
        // If both are pinned (shouldn't happen with single pinned stat)
        if (aIsPinned && bIsPinned) return 0;
      }
      
      // For metric order, use custom order if available, otherwise default order
      // Note: Week filtering doesn't change metric positions, just highlights the selected week
      const orderToUse = mainStatOrder.length > 0 ? mainStatOrder : ['Total Sales', ...filteredMetrics.filter(m => m.label !== 'Total Sales').map(m => m.label).sort()];
      const aIndex = orderToUse.indexOf(a.name);
      const bIndex = orderToUse.indexOf(b.name);
      
      // If both stats are in the order array, use that order
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      
      // If only one is in the order array, prioritize it
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      
      // If neither is in the order array, fall back to alphabetical
      return a.name.localeCompare(b.name);
    });
  };

  // Handle week column sorting for parent company table
  const handleParentWeekSort = (weekColumn) => {
    setParentWeekSort(prevSort => {
      if (prevSort.column === weekColumn) {
        // Toggle direction if same column
        return {
          column: weekColumn,
          direction: prevSort.direction === 'asc' ? 'desc' : 'asc'
        };
      } else {
        // New column, default to descending (highest values first)
        return {
          column: weekColumn,
          direction: 'desc'
        };
      }
    });
  };

  // Handle week column sorting for companies table
  const handleCompaniesWeekSort = (weekColumn) => {
    setCompaniesWeekSort(prevSort => {
      if (prevSort.column === weekColumn) {
        return {
          column: weekColumn,
          direction: prevSort.direction === 'asc' ? 'desc' : 'asc'
        };
      } else {
        return {
          column: weekColumn,
          direction: 'desc'
        };
      }
    });
  };

  // Handle week column sorting for groups table
  const handleGroupsWeekSort = (weekColumn) => {
    setGroupsWeekSort(prevSort => {
      if (prevSort.column === weekColumn) {
        return {
          column: weekColumn,
          direction: prevSort.direction === 'asc' ? 'desc' : 'asc'
        };
      } else {
        return {
          column: weekColumn,
          direction: 'desc'
        };
      }
    });
  };

  // Render Company/Seller Accounts only
  const renderCompanies = () => {
    const companyAccounts = data.companyAccounts?.['jungle-group'];
    if (!companyAccounts) return null;

    const columns = getColumns();
    const filteredMetrics = getFilteredMetrics();

    return (
      <div className="relative">
        <div ref={companiesTableRef} className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-200px)]">
          <table className="w-full table-fixed">
            <thead className="bg-gradient-to-r from-secondary-50 to-secondary-100 sticky top-0 z-50">
              <tr>
                {columns.map((column, colIndex) => (
                  <th 
                    key={column.key} 
                    className={`py-3 ${column.align === 'center' ? 'text-center' : 'text-left'} text-xs font-semibold text-secondary-700 uppercase tracking-wider ${column.width} border-b border-secondary-200 ${
                      column.key === 'name' ? 'sticky left-0 z-40 bg-gradient-to-r from-secondary-50 to-secondary-100 px-4' : 'bg-gradient-to-r from-secondary-50 to-secondary-100 px-1'
                    } ${column.key.startsWith('week') ? 'cursor-pointer hover:bg-secondary-100 transition-colors' : ''}`}
                    style={{
                      ...column.key === 'name' ? { minWidth: '192px', left: '0' } : { minWidth: '80px' },
                      backgroundColor: column.key === 'name' ? '#f8fafc' : undefined
                    }}
                    onClick={column.key.startsWith('week') ? () => handleCompaniesWeekSort(column.key) : undefined}
                    title={column.key.startsWith('week') ? 
                      `Click to sort companies by ${column.key}${mainPinnedStat ? ` (using ${mainPinnedStat})` : ''}` : undefined}
                  >
                    <div className={`flex items-center ${column.align === 'center' ? 'justify-center' : ''} space-x-1`}>
                      {column.label}
                      {column.hasIndicator && (
                        <div className="w-1.5 h-1.5 bg-primary-400 rounded-full"></div>
                      )}
                      {/* Show sort indicator for week columns */}
                      {column.key.startsWith('week') && companiesWeekSort.column === column.key && (
                        <svg 
                          className={`w-3 h-3 transform transition-transform duration-200 ${companiesWeekSort.direction === 'asc' ? '' : 'rotate-180'}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      )}
                      {/* Show active week sort indicator */}
                      {column.key.startsWith('week') && companiesWeekSort.column === column.key && (
                        <div className="flex items-center space-x-1">
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                          {/* Show pin indicator when using pinned metric for week sorting */}
                          {mainPinnedStat && (
                            <svg className="w-2.5 h-2.5 text-primary-600" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 4l-8 8h6v8h4v-8h6z" />
                            </svg>
                          )}
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-100">
              {Object.values(companyAccounts)
                .filter(company => shouldShowCompany(company.id))
                .sort((a, b) => {
                  // If week column filtering is active, sort companies by that week's values
                  if (companiesWeekSort.column && companiesWeekSort.column.startsWith('week')) {
                    // If there's a pinned metric, use that metric for week-based company sorting
                    if (mainPinnedStat) {
                      const pinnedMetric = filteredMetrics.find(m => m.label === mainPinnedStat);
                      if (pinnedMetric && a.data && b.data) {
                        const aValue = a.data[pinnedMetric.key]?.[companiesWeekSort.column] || 0;
                        const bValue = b.data[pinnedMetric.key]?.[companiesWeekSort.column] || 0;
                        
                        // Convert to numbers for proper sorting
                        const aNum = typeof aValue === 'string' ? parseFloat(aValue.replace(/[,%$]/g, '')) : aValue;
                        const bNum = typeof bValue === 'string' ? parseFloat(bValue.replace(/[,%$]/g, '')) : bValue;
                        
                        const result = companiesWeekSort.direction === 'asc' ? aNum - bNum : bNum - aNum;
                        // If values are equal, fall back to name sorting for consistent order
                        return result !== 0 ? result : a.name.localeCompare(b.name);
                      }
                    } else {
                      // If no pinned metric, use the first available metric for week-based sorting
                      const firstMetric = filteredMetrics[0];
                      if (firstMetric && a.data && b.data) {
                        const aValue = a.data[firstMetric.key]?.[companiesWeekSort.column] || 0;
                        const bValue = b.data[firstMetric.key]?.[companiesWeekSort.column] || 0;
                        
                        // Convert to numbers for proper sorting
                        const aNum = typeof aValue === 'string' ? parseFloat(aValue.replace(/[,%$]/g, '')) : aValue;
                        const bNum = typeof bValue === 'string' ? parseFloat(bValue.replace(/[,%$]/g, '')) : bValue;
                        
                        const result = companiesWeekSort.direction === 'asc' ? aNum - bNum : bNum - aNum;
                        // If values are equal, fall back to name sorting for consistent order
                        return result !== 0 ? result : a.name.localeCompare(b.name);
                      }
                    }
                  }
                  
                  // Default sorting by name
                  return sortOrder === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
                })
                .map((company, companyIndex) => (
                <React.Fragment key={company.id}>
                  {/* Company Header Row */}
                  <tr 
                    className="bg-blue-100 border-b-2 border-blue-200 cursor-pointer hover:bg-blue-200 transition-all duration-200" 
                    onClick={() => toggleCompany(company.id)}
                  >
                    <td className="px-4 py-3 sticky left-0 z-20 bg-blue-100 hover:bg-blue-200 min-w-[300px] shadow-[2px_0_8px_rgba(0,0,0,0.1)]">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-7 h-7 bg-primary-500 rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                              {company.name.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-blue-900">{company.name}</h4>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div 
                            className="text-blue-700 hover:text-blue-900 transition-colors duration-200"
                            aria-label={collapsedCompanies.has(company.id) ? "Expand" : "Collapse"}
                          >
                            <svg 
                              className={`w-5 h-5 transform transition-transform duration-200 ${collapsedCompanies.has(company.id) ? '' : 'rotate-180'}`} 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </td>
                    {/* Add remaining columns to match table structure */}
                    {columns.slice(1).map((column, colIndex) => (
                      <td key={column.key} className="px-1 py-3 bg-blue-100">
                        {/* Empty cell for non-name columns */}
                      </td>
                    ))}
                  </tr>

                  {/* Company Data Rows */}
                  {!collapsedCompanies.has(company.id) && (
                    <>
                      {/* Company-level data if available */}
                      {company.data && 
                        // Sort metrics with pinned stats always at the top, maintain metric order regardless of week filtering
                        filteredMetrics
                          .sort((a, b) => {
                            // ALWAYS check pinned stat first - pinned stats go to the top regardless of other sorting
                            if (mainPinnedStat) {
                              const aIsPinned = a.label === mainPinnedStat;
                              const bIsPinned = b.label === mainPinnedStat;
                              
                              if (aIsPinned && !bIsPinned) return -1; // Pinned item goes to top
                              if (!aIsPinned && bIsPinned) return 1;  // Pinned item goes to top
                              
                              if (aIsPinned && bIsPinned) return 0;
                            }
                            
                            // For metric order, use custom order if available, otherwise default order
                            // Note: Week filtering doesn't change metric positions, just highlights the selected week
                            const orderToUse = mainStatOrder.length > 0 ? mainStatOrder : ['Total Sales', ...filteredMetrics.filter(m => m.label !== 'Total Sales').map(m => m.label).sort()];
                            const aIndex = orderToUse.indexOf(a.label);
                            const bIndex = orderToUse.indexOf(b.label);
                            
                            if (aIndex !== -1 && bIndex !== -1) {
                              return aIndex - bIndex;
                            }
                            
                            if (aIndex !== -1) return -1;
                            if (bIndex !== -1) return 1;
                            
                            return a.label.localeCompare(b.label);
                          })
                          .map((metric, metricIndex) => (
                        <tr 
                          key={`${company.id}-company-${metric.key}`} 
                          draggable={true}
                          onDragStart={(e) => {
                            setMainDraggedStat(metric.label);
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', metric.label);
                            e.currentTarget.style.opacity = '0.5';
                          }}
                          onDragOver={(e) => {
                            if (mainDraggedStat && mainDraggedStat !== metric.label) {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                              
                              const rect = e.currentTarget.getBoundingClientRect();
                              const midY = rect.top + rect.height / 2;
                              const position = e.clientY < midY ? 'above' : 'below';
                              
                              setMainDragOverStat(metric.label);
                              setMainDragPosition(position);
                            }
                          }}
                          onDragEnter={(e) => {
                            if (mainDraggedStat && mainDraggedStat !== metric.label) {
                              e.preventDefault();
                              
                              const rect = e.currentTarget.getBoundingClientRect();
                              const midY = rect.top + rect.height / 2;
                              const position = e.clientY < midY ? 'above' : 'below';
                              
                              setMainDragOverStat(metric.label);
                              setMainDragPosition(position);
                            }
                          }}
                          onDragLeave={(e) => {
                            if (!e.currentTarget.contains(e.relatedTarget)) {
                              setMainDragOverStat(null);
                              setMainDragPosition(null);
                            }
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            
                            if (mainDraggedStat && mainDraggedStat !== metric.label) {
                              const currentOrder = mainStatOrder.length > 0 ? [...mainStatOrder] : 
                                ['Total Sales', ...filteredMetrics.filter(m => m.label !== 'Total Sales').map(m => m.label).sort()];
                              
                              const newOrder = currentOrder.filter(stat => stat !== mainDraggedStat);
                              const targetIndex = newOrder.indexOf(metric.label);
                              
                              if (targetIndex !== -1) {
                                const insertIndex = mainDragPosition === 'above' ? targetIndex : targetIndex + 1;
                                newOrder.splice(insertIndex, 0, mainDraggedStat);
                              } else {
                                newOrder.push(mainDraggedStat);
                              }
                              
                              setMainStatOrder(newOrder);
                            }
                            setMainDraggedStat(null);
                            setMainDragOverStat(null);
                            setMainDragPosition(null);
                          }}
                          onDragEnd={(e) => {
                            setMainDraggedStat(null);
                            setMainDragOverStat(null);
                            setMainDragPosition(null);
                            e.currentTarget.style.opacity = '';
                          }}
                          className={`
                            group transition-all duration-200 cursor-move
                            ${mainPinnedStat === metric.label ? 'bg-primary-50 hover:bg-primary-100' : 'bg-white hover:bg-secondary-50'}
                            ${mainDraggedStat === metric.label ? 'opacity-50 bg-primary-50' : ''}
                            ${mainDragOverStat === metric.label && mainDragPosition === 'above' ? 'border-t-4 border-blue-500' : ''}
                            ${mainDragOverStat === metric.label && mainDragPosition === 'below' ? 'border-b-4 border-blue-500' : ''}
                            ${metricIndex % 2 === 0 ? '' : 'bg-secondary-25'}
                          `}
                        >
                          <td 
                            className="py-2 text-sm whitespace-nowrap px-4" 
                            style={{ 
                              backgroundColor: mainPinnedStat === metric.label ? '#fef3f2' : 
                                (metricIndex % 2 === 0 ? '#ffffff' : '#fafafa'),
                              position: 'sticky',
                              left: 0,
                              zIndex: 20,
                              boxShadow: '2px 0 4px rgba(0, 0, 0, 0.1)'
                            }}
                            onMouseEnter={() => setMainHoveredStat(metric.label)}
                            onMouseLeave={() => setMainHoveredStat(null)}
                          >
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => {
                                  if (mainPinnedStat === metric.label) {
                                    setMainPinnedStat(null);
                                  } else {
                                    setMainPinnedStat(metric.label);
                                  }
                                }}
                                className={`
                                  ${(mainHoveredStat === metric.label || mainPinnedStat === metric.label) ? 'opacity-100' : 'opacity-0'}
                                  group-hover:opacity-100 transition-opacity duration-200
                                  ${mainPinnedStat === metric.label ? 'text-primary-600' : 'text-secondary-400 hover:text-secondary-600'}
                                `}
                                title={mainPinnedStat === metric.label ? 'Unpin stat' : 'Pin stat to top'}
                              >
                                <svg 
                                  className={`w-4 h-4 transform transition-transform duration-200 ${mainPinnedStat === metric.label ? 'rotate-180' : ''}`} 
                                  fill="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M12 4l-8 8h6v8h4v-8h6z" />
                                </svg>
                              </button>
                            <span className="font-medium text-secondary-900">{metric.label}</span>
                            </div>
                          </td>
                          {/* Single data column for Sept 7-13, 2025 */}
                          <td 
                            className="py-2 px-1 text-sm whitespace-nowrap text-center"
                            style={{
                              backgroundColor: metricIndex % 2 === 0 ? 'white' : 'var(--color-secondary-25)'
                            }}
                          >
                            <span className={getMetricColor(metric.label, formatValue(company.data[metric.key]?.week1, metric.type))}>
                              {formatValue(company.data[metric.key]?.week1, metric.type)}
                            </span>
                          </td>
                        </tr>
                      ))}

                      {/* No data message for companies without data */}
                      {!company.data && (
                        <tr className="bg-secondary-25">
                          <td colSpan={columns.length} className="px-4 py-2 text-center text-sm text-secondary-500">
                            No data available for this company
                          </td>
                        </tr>
                      )}

                      {/* Spacer row between companies */}
                      {companyIndex < Object.values(companyAccounts).filter(company => shouldShowCompany(company.id)).length - 1 && (
                        <tr>
                          <td colSpan={columns.length} className="h-3 bg-secondary-25"></td>
                        </tr>
                      )}
                    </>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Render all Groups combined with individual collapsible sections
  const renderGroups = () => {
    const companyAccounts = data.companyAccounts?.['jungle-group'];
    if (!companyAccounts) return null;

    // Collect all groups from all companies and apply filtering
    const allGroups = [];
    Object.values(companyAccounts).forEach(company => {
      if (company.groups && company.groups.length > 0) {
        company.groups.forEach(group => {
          if (shouldShowGroup(group.id)) {
            allGroups.push({
              ...group,
              companyName: company.name
            });
          }
        });
      }
    });

    // Sort groups based on week filtering or sort order
    allGroups.sort((a, b) => {
      // If week column filtering is active, sort groups by that week's values
      if (groupsWeekSort.column && groupsWeekSort.column.startsWith('week')) {
        // If there's a pinned metric, use that metric for week-based group sorting
        if (mainPinnedStat) {
          const pinnedMetric = filteredMetrics.find(m => m.label === mainPinnedStat);
          if (pinnedMetric && a.data && b.data) {
            const aValue = a.data[pinnedMetric.key]?.[groupsWeekSort.column] || 0;
            const bValue = b.data[pinnedMetric.key]?.[groupsWeekSort.column] || 0;
            
            // Convert to numbers for proper sorting
            const aNum = typeof aValue === 'string' ? parseFloat(aValue.replace(/[,%$]/g, '')) : aValue;
            const bNum = typeof bValue === 'string' ? parseFloat(bValue.replace(/[,%$]/g, '')) : bValue;
            
            const result = groupsWeekSort.direction === 'asc' ? aNum - bNum : bNum - aNum;
            // If values are equal, fall back to name sorting for consistent order
            return result !== 0 ? result : a.name.localeCompare(b.name);
          }
        } else {
          // If no pinned metric, use the first available metric for week-based sorting
          const firstMetric = filteredMetrics[0];
          if (firstMetric && a.data && b.data) {
            const aValue = a.data[firstMetric.key]?.[groupsWeekSort.column] || 0;
            const bValue = b.data[firstMetric.key]?.[groupsWeekSort.column] || 0;
            
            // Convert to numbers for proper sorting
            const aNum = typeof aValue === 'string' ? parseFloat(aValue.replace(/[,%$]/g, '')) : aValue;
            const bNum = typeof bValue === 'string' ? parseFloat(bValue.replace(/[,%$]/g, '')) : bValue;
            
            const result = groupsWeekSort.direction === 'asc' ? aNum - bNum : bNum - aNum;
            // If values are equal, fall back to name sorting for consistent order
            return result !== 0 ? result : a.name.localeCompare(b.name);
          }
        }
      }
      
      // Default sorting by name
      return sortOrder === 'asc' 
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    });

    if (allGroups.length === 0) {
      return (
        <div className="p-6 text-center text-secondary-500">
          No groups available
        </div>
      );
    }

    const columns = getColumns();
    const filteredMetrics = getFilteredMetrics();

    return (
      <div className="relative">
        <div ref={groupsTableRef} className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-200px)]">
          <table className="w-full table-fixed">
            <thead className="bg-gradient-to-r from-secondary-50 to-secondary-100 sticky top-0 z-50">
              <tr>
                {columns.map((column, colIndex) => (
                  <th 
                    key={column.key} 
                    className={`py-3 ${column.align === 'center' ? 'text-center' : 'text-left'} text-xs font-semibold text-secondary-700 uppercase tracking-wider ${column.width} border-b border-secondary-200 ${
                      column.key === 'name' ? 'sticky left-0 z-40 bg-gradient-to-r from-secondary-50 to-secondary-100 px-4' : 'bg-gradient-to-r from-secondary-50 to-secondary-100 px-1'
                    } ${column.key.startsWith('week') ? 'cursor-pointer hover:bg-secondary-100 transition-colors' : ''}`}
                    style={{
                      ...column.key === 'name' ? { minWidth: '192px', left: '0' } : { minWidth: '80px' },
                      backgroundColor: column.key === 'name' ? '#f8fafc' : undefined
                    }}
                    onClick={column.key.startsWith('week') ? () => handleGroupsWeekSort(column.key) : undefined}
                    title={column.key.startsWith('week') ? 
                      `Click to sort groups by ${column.key}${mainPinnedStat ? ` (using ${mainPinnedStat})` : ''}` : undefined}
                  >
                    <div className={`flex items-center ${column.align === 'center' ? 'justify-center' : ''} space-x-1`}>
                      {column.label}
                      {column.hasIndicator && (
                        <div className="w-1.5 h-1.5 bg-primary-400 rounded-full"></div>
                      )}
                      {/* Show sort indicator for week columns */}
                      {column.key.startsWith('week') && groupsWeekSort.column === column.key && (
                        <svg 
                          className={`w-3 h-3 transform transition-transform duration-200 ${groupsWeekSort.direction === 'asc' ? '' : 'rotate-180'}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      )}
                      {/* Show active week sort indicator */}
                      {column.key.startsWith('week') && groupsWeekSort.column === column.key && (
                        <div className="flex items-center space-x-1">
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                          {/* Show pin indicator when using pinned metric for week sorting */}
                          {mainPinnedStat && (
                            <svg className="w-2.5 h-2.5 text-primary-600" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 4l-8 8h6v8h4v-8h6z" />
                            </svg>
                          )}
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-100">
              {allGroups.map((group, groupIndex) => (
                <React.Fragment key={group.id}>
                  {/* Group Header Row */}
                  <tr 
                    className="bg-blue-100 border-b-2 border-blue-200 cursor-pointer hover:bg-blue-200 transition-all duration-200" 
                    onClick={() => toggleGroup(group.id)}
                  >
                    <td className="px-4 py-3 sticky left-0 z-20 bg-blue-100 hover:bg-blue-200 min-w-[300px] shadow-[2px_0_8px_rgba(0,0,0,0.1)]">
                      <div className="flex items-center space-x-3">
                        <div className="w-7 h-7 bg-secondary-500 rounded-lg flex items-center justify-center">
                          <span className="text-white font-bold text-sm">G</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-base font-semibold text-blue-900 truncate">{group.name}</h4>
                          <p className="text-xs text-blue-700 truncate">from {group.companyName}</p>
                        </div>
                      </div>
                    </td>
                    {/* Data column with Parent/Child buttons and caret on the right */}
                    <td className="px-4 py-3 bg-blue-100">
                      <div className="flex items-center justify-end space-x-2">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleParentClick(group);
                          }}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-md shadow-sm transition-colors border border-primary-600 min-w-[60px]">
                          Parent
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleChildClick(group);
                          }}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-secondary-500 hover:bg-secondary-600 rounded-md shadow-sm transition-colors border border-secondary-600 min-w-[60px]">
                          Child
                        </button>
                        <div 
                          className="text-blue-700 hover:text-blue-900 transition-colors duration-200 cursor-pointer"
                          aria-label={collapsedGroups.has(group.id) ? "Expand" : "Collapse"}
                        >
                          <svg 
                            className={`w-5 h-5 transform transition-transform duration-200 ${collapsedGroups.has(group.id) ? '' : 'rotate-180'}`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </td>
                  </tr>

                  {/* Group Data Rows */}
                  {!collapsedGroups.has(group.id) && (
                    <>
                      {/* Group metrics */}
                      {group.data && 
                        // Sort metrics with pinned stats always at the top, maintain metric order regardless of week filtering
                        filteredMetrics
                          .sort((a, b) => {
                            // ALWAYS check pinned stat first - pinned stats go to the top regardless of other sorting
                            if (mainPinnedStat) {
                              const aIsPinned = a.label === mainPinnedStat;
                              const bIsPinned = b.label === mainPinnedStat;
                              
                              if (aIsPinned && !bIsPinned) return -1; // Pinned item goes to top
                              if (!aIsPinned && bIsPinned) return 1;  // Pinned item goes to top
                              
                              if (aIsPinned && bIsPinned) return 0;
                            }
                            
                            // For metric order, use custom order if available, otherwise default order
                            // Note: Week filtering doesn't change metric positions, just highlights the selected week
                            const orderToUse = mainStatOrder.length > 0 ? mainStatOrder : ['Total Sales', ...filteredMetrics.filter(m => m.label !== 'Total Sales').map(m => m.label).sort()];
                            const aIndex = orderToUse.indexOf(a.label);
                            const bIndex = orderToUse.indexOf(b.label);
                            
                            if (aIndex !== -1 && bIndex !== -1) {
                              return aIndex - bIndex;
                            }
                            
                            if (aIndex !== -1) return -1;
                            if (bIndex !== -1) return 1;
                            
                            return a.label.localeCompare(b.label);
                          })
                          .map((metric, metricIndex) => (
                        <tr 
                          key={`${group.id}-${metric.key}`} 
                          draggable={true}
                          onDragStart={(e) => {
                            setMainDraggedStat(metric.label);
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', metric.label);
                            e.currentTarget.style.opacity = '0.5';
                          }}
                          onDragOver={(e) => {
                            if (mainDraggedStat && mainDraggedStat !== metric.label) {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                              
                              const rect = e.currentTarget.getBoundingClientRect();
                              const midY = rect.top + rect.height / 2;
                              const position = e.clientY < midY ? 'above' : 'below';
                              
                              setMainDragOverStat(metric.label);
                              setMainDragPosition(position);
                            }
                          }}
                          onDragEnter={(e) => {
                            if (mainDraggedStat && mainDraggedStat !== metric.label) {
                              e.preventDefault();
                              
                              const rect = e.currentTarget.getBoundingClientRect();
                              const midY = rect.top + rect.height / 2;
                              const position = e.clientY < midY ? 'above' : 'below';
                              
                              setMainDragOverStat(metric.label);
                              setMainDragPosition(position);
                            }
                          }}
                          onDragLeave={(e) => {
                            if (!e.currentTarget.contains(e.relatedTarget)) {
                              setMainDragOverStat(null);
                              setMainDragPosition(null);
                            }
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            
                            if (mainDraggedStat && mainDraggedStat !== metric.label) {
                              const currentOrder = mainStatOrder.length > 0 ? [...mainStatOrder] : 
                                ['Total Sales', ...filteredMetrics.filter(m => m.label !== 'Total Sales').map(m => m.label).sort()];
                              
                              const newOrder = currentOrder.filter(stat => stat !== mainDraggedStat);
                              const targetIndex = newOrder.indexOf(metric.label);
                              
                              if (targetIndex !== -1) {
                                const insertIndex = mainDragPosition === 'above' ? targetIndex : targetIndex + 1;
                                newOrder.splice(insertIndex, 0, mainDraggedStat);
                              } else {
                                newOrder.push(mainDraggedStat);
                              }
                              
                              setMainStatOrder(newOrder);
                            }
                            setMainDraggedStat(null);
                            setMainDragOverStat(null);
                            setMainDragPosition(null);
                          }}
                          onDragEnd={(e) => {
                            setMainDraggedStat(null);
                            setMainDragOverStat(null);
                            setMainDragPosition(null);
                            e.currentTarget.style.opacity = '';
                          }}
                          className={`
                            group transition-all duration-200 cursor-move
                            ${mainPinnedStat === metric.label ? 'bg-primary-50 hover:bg-primary-100' : 'bg-white hover:bg-secondary-50'}
                            ${mainDraggedStat === metric.label ? 'opacity-50 bg-primary-50' : ''}
                            ${mainDragOverStat === metric.label && mainDragPosition === 'above' ? 'border-t-4 border-blue-500' : ''}
                            ${mainDragOverStat === metric.label && mainDragPosition === 'below' ? 'border-b-4 border-blue-500' : ''}
                            ${metricIndex % 2 === 0 ? '' : 'bg-secondary-25'}
                          `}
                        >
                          <td 
                            className="py-2 text-sm whitespace-nowrap px-4" 
                            style={{ 
                              backgroundColor: mainPinnedStat === metric.label ? '#fef3f2' : 
                                (metricIndex % 2 === 0 ? '#ffffff' : '#fafafa'),
                              position: 'sticky',
                              left: 0,
                              zIndex: 20,
                              boxShadow: '2px 0 4px rgba(0, 0, 0, 0.1)'
                            }}
                            onMouseEnter={() => setMainHoveredStat(metric.label)}
                            onMouseLeave={() => setMainHoveredStat(null)}
                          >
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => {
                                  if (mainPinnedStat === metric.label) {
                                    setMainPinnedStat(null);
                                  } else {
                                    setMainPinnedStat(metric.label);
                                  }
                                }}
                                className={`
                                  ${(mainHoveredStat === metric.label || mainPinnedStat === metric.label) ? 'opacity-100' : 'opacity-0'}
                                  group-hover:opacity-100 transition-opacity duration-200
                                  ${mainPinnedStat === metric.label ? 'text-primary-600' : 'text-secondary-400 hover:text-secondary-600'}
                                `}
                                title={mainPinnedStat === metric.label ? 'Unpin stat' : 'Pin stat to top'}
                              >
                                <svg 
                                  className={`w-4 h-4 transform transition-transform duration-200 ${mainPinnedStat === metric.label ? 'rotate-180' : ''}`} 
                                  fill="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M12 4l-8 8h6v8h4v-8h6z" />
                                </svg>
                              </button>
                            <span className="font-medium text-secondary-900">{metric.label}</span>
                            </div>
                          </td>
                          {/* Single data column for Sept 7-13, 2025 */}
                          <td 
                            className="py-2 px-1 text-sm whitespace-nowrap text-center"
                            style={{
                              backgroundColor: metricIndex % 2 === 0 ? 'white' : 'var(--color-secondary-25)'
                            }}
                          >
                            <span className={getMetricColor(metric.label, formatValue(group.data[metric.key]?.week1, metric.type))}>
                              {formatValue(group.data[metric.key]?.week1, metric.type)}
                            </span>
                          </td>
                        </tr>
                      ))}

                      {/* No data message for groups without data */}
                      {!group.data && (
                        <tr className="bg-secondary-25">
                          <td colSpan={columns.length} className="px-4 py-2 text-center text-sm text-secondary-500">
                            No data available for this group
                          </td>
                        </tr>
                      )}

                      {/* Spacer row between groups */}
                      {groupIndex < allGroups.length - 1 && (
                        <tr>
                          <td colSpan={columns.length} className="h-3 bg-secondary-25"></td>
                        </tr>
                      )}
                    </>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const handleParentClick = (group) => {
    const newDetailView = { type: 'parent', groupId: group.id, groupName: group.name, companyName: group.companyName };
    setDetailView(newDetailView);
    setParentFilter(null); // Clear parent filter when switching to parent view
    setColumnFilters({ brand: [], product: [], custom: [] }); // Reset column filters
    setDetailSortConfig({ key: null, direction: 'asc' }); // Reset sorting
    setCollapsedProducts(new Set()); // Reset collapsed state
    setDetailSelectedMetrics(new Set(['totalSales', 'unitsSold', 'tacos', 'conversion', 'organicSales'])); // Reset selected metrics to default
    if (onDetailViewChange) {
      onDetailViewChange(newDetailView);
    }
  };

  const handleChildClick = (group) => {
    const newDetailView = { type: 'child', groupId: group.id, groupName: group.name, companyName: group.companyName };
    setDetailView(newDetailView);
    setParentFilter(null); // Clear parent filter when manually switching to child view
    setColumnFilters({ brand: [], product: [], custom: [] }); // Reset column filters
    setDetailSortConfig({ key: null, direction: 'asc' }); // Reset sorting
    setCollapsedProducts(new Set()); // Reset collapsed state
    setDetailSelectedMetrics(new Set(['totalSales', 'unitsSold', 'tacos', 'conversion', 'organicSales'])); // Reset selected metrics to default
    if (onDetailViewChange) {
      onDetailViewChange(newDetailView);
    }
  };

  const handleBackToGroups = () => {
    setDetailView(null);
    setParentFilter(null); // Clear parent filter when going back
    setColumnFilters({ brand: [], product: [], custom: [] }); // Reset column filters
    setDetailSortConfig({ key: null, direction: 'asc' }); // Reset sorting
    setCollapsedProducts(new Set()); // Reset collapsed state
    setDetailSelectedMetrics(new Set(['totalSales', 'unitsSold', 'tacos', 'conversion', 'organicSales'])); // Reset selected metrics to default
    if (onDetailViewChange) {
      onDetailViewChange(null);
    }
  };

  const handleViewParentChildren = (parentProductName, groupId, groupName) => {
    // Switch to child view and filter by this parent
    const newDetailView = { type: 'child', groupId, groupName };
    setDetailView(newDetailView);
    setParentFilter(parentProductName); // Keep the parent filter for this case
    setColumnFilters({ brand: [], product: [], custom: [] }); // Reset column filters
    setDetailSortConfig({ key: null, direction: 'asc' }); // Reset sorting
    setCollapsedProducts(new Set()); // Reset collapsed state
    setDetailSelectedMetrics(new Set(['totalSales', 'unitsSold', 'tacos', 'conversion', 'organicSales'])); // Reset selected metrics to default
    if (onDetailViewChange) {
      onDetailViewChange(newDetailView);
    }
  };

  // Handle metric selection for detailed view
  const handleDetailMetricToggle = (metricKey) => {
    setDetailSelectedMetrics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(metricKey)) {
        newSet.delete(metricKey);
      } else {
        newSet.add(metricKey);
      }
      return newSet;
    });
  };

  // Define stats for Parent and Child views
  const parentStats = [
    { key: 'totalSales', label: 'Total Sales', type: 'currency' },
    { key: 'unitsSold', label: 'Units Sold', type: 'number' },
    { key: 'tacos', label: 'TACOS', type: 'percentage' },
    { key: 'conversion', label: 'Conversion', type: 'percentage' },
    { key: 'organicSales', label: 'Organic Sales %', type: 'percentage' },
    { key: 'adImpressions', label: 'Ad Impressions', type: 'number' },
    { key: 'adCpc', label: 'Ad CPC', type: 'currency' },
    { key: 'adClicks', label: 'Ad Clicks', type: 'number' },
    { key: 'adSales', label: 'Ad Sales', type: 'currency' },
    { key: 'adOrders', label: 'Ad Orders', type: 'number' },
    { key: 'adSpend', label: 'Ad Spend', type: 'currency' },
    { key: 'adSpendPerUnit', label: 'Ad Spend per unit', type: 'currency' },
    { key: 'acos', label: 'ACOS', type: 'percentage' },
    { key: 'totalReferralFees', label: 'Total Referral Fees', type: 'currency' },
    { key: 'inventory', label: 'Inventory', type: 'number' },
    { key: 'bsr', label: 'BSR', type: 'number' },
    { key: 'cogs', label: 'COGS', type: 'currency' },
    { key: 'price', label: 'Price', type: 'currency' },
    { key: 'avgSalePrice', label: 'AVG Sale Price', type: 'currency' },
    { key: 'totalReferralFeesPercent', label: 'Total Referral Fees', type: 'currency' },
    { key: 'totalFbaFees', label: 'Total FBA Fees', type: 'currency' },
    { key: 'totalFees', label: 'Total Fees', type: 'currency' },
    { key: 'storageFees', label: 'Storage Fees', type: 'currency' },
    { key: 'inboundShippingFees', label: 'Inbound Shipping Fees', type: 'currency' },
    { key: 'profitPerUnit', label: 'Profit per unit', type: 'currency' },
    { key: 'profitPercent', label: 'Profit Percentage', type: 'percentage' },
    { key: 'profitTotal', label: 'Profit Total', type: 'currency' }
  ];

  const childStats = [
    { key: 'totalSales', label: 'Total Sales', type: 'currency' },
    { key: 'unitsSold', label: 'Units Sold', type: 'number' },
    { key: 'tacos', label: 'TACOS', type: 'percentage' },
    { key: 'conversion', label: 'Conversion', type: 'percentage' },
    { key: 'organicSales', label: 'Organic Sales %', type: 'percentage' },
    { key: 'adImpressions', label: 'Ad Impressions', type: 'number' },
    { key: 'adCpc', label: 'Ad CPC', type: 'currency' },
    { key: 'adClicks', label: 'Ad Clicks', type: 'number' },
    { key: 'adSales', label: 'Ad Sales', type: 'currency' },
    { key: 'adOrders', label: 'Ad Orders', type: 'number' },
    { key: 'adSpend', label: 'Ad Spend', type: 'currency' },
    { key: 'adSpendPerUnit', label: 'Ad Spend per unit', type: 'currency' },
    { key: 'acos', label: 'ACOS', type: 'percentage' },
    { key: 'totalReferralFees', label: 'Total Referral Fees', type: 'currency' },
    { key: 'inventory', label: 'Inventory', type: 'number' },
    { key: 'bsr', label: 'BSR', type: 'number' },
    { key: 'cogs', label: 'COGS', type: 'currency' },
    { key: 'price', label: 'Price', type: 'currency' },
    { key: 'avgSalePrice', label: 'AVG Sale Price', type: 'currency' },
    { key: 'totalReferralFeesPercent', label: 'Total Referral Fees', type: 'currency' },
    { key: 'totalFbaFees', label: 'Total FBA Fees', type: 'currency' },
    { key: 'totalFees', label: 'Total Fees', type: 'currency' },
    { key: 'storageFees', label: 'Storage Fees', type: 'currency' },
    { key: 'inboundShippingFees', label: 'Inbound Shipping Fees', type: 'currency' },
    { key: 'profitPerUnit', label: 'Profit per unit', type: 'currency' },
    { key: 'profitPercent', label: 'Profit Percentage', type: 'percentage' },
    { key: 'profitTotal', label: 'Profit Total', type: 'currency' }
  ];

  const handleDetailSelectAllMetrics = () => {
    const allMetricKeys = (detailView?.type === 'parent' ? parentStats : childStats).map(stat => stat.key);
    setDetailSelectedMetrics(new Set(allMetricKeys));
  };

  const handleDetailDeselectAllMetrics = () => {
    setDetailSelectedMetrics(new Set());
  };

  // Render metric filter dropdown for stat column
  const StatDropdown = React.forwardRef(({ position }, ref) => {
    if (!detailView) return null;

    const dropdown = (
      <div 
        ref={ref}
        className="fixed z-[9999] w-80 bg-white rounded-lg shadow-lg border border-secondary-200"
        style={{
          top: position.top || 0,
          left: position.left || 0,
        }}
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={handleDetailSelectAllMetrics}
                className="px-2 py-1 text-xs font-medium text-white bg-primary-500 hover:bg-primary-600 rounded transition-colors"
              >
                Select All
              </button>
              <button
                onClick={handleDetailDeselectAllMetrics}
                className="px-2 py-1 text-xs font-medium text-secondary-600 bg-secondary-100 hover:bg-secondary-200 rounded transition-colors"
              >
                Clear All
              </button>
            </div>
            <div className="text-xs text-secondary-600">
              {detailSelectedMetrics.size} of {(detailView.type === 'parent' ? parentStats : childStats).length} selected
            </div>
          </div>
          
          <div className="max-h-64 overflow-y-auto">
            <div className="space-y-2">
              {(detailView.type === 'parent' ? parentStats : childStats).map((stat) => (
                <label
                  key={stat.key}
                  className="flex items-center space-x-2 p-1 rounded hover:bg-secondary-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={detailSelectedMetrics.has(stat.key)}
                    onChange={() => handleDetailMetricToggle(stat.key)}
                    className="w-3 h-3 text-primary-600 bg-white border-secondary-300 rounded focus:ring-primary-500 focus:ring-1"
                  />
                  <span className="text-xs text-secondary-900 font-medium truncate">
                    {stat.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    );

    return <Portal>{dropdown}</Portal>;
  });

  // Handle sorting for detailed view
  const handleDetailSort = (columnKey) => {
    setDetailSortConfig(prevConfig => {
      if (prevConfig.key === columnKey) {
        // Toggle direction if same column
        return {
          key: columnKey,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      } else {
        // New column, default to descending (highest first)
        return {
          key: columnKey,
          direction: 'desc'
        };
      }
    });
  };

  // Sort products by pinned stat values for a specific week (defaults to Total Sales if no stat pinned)
  const sortProductsByWeeklyStat = (products, weekKey, direction) => {
    // Use pinned stat or default to "Total Sales"
    const statToSortBy = pinnedStat || 'Total Sales';
    
    return [...products].sort((a, b) => {
      // Find the stat data for each product
      const statKey = parentStats.find(s => s.label === statToSortBy)?.key || childStats.find(s => s.label === statToSortBy)?.key;
      const aStatData = a.stats[statKey];
      const bStatData = b.stats[statKey];
      
      const aValue = aStatData?.[weekKey] || 0;
      const bValue = bStatData?.[weekKey] || 0;
      
      // Convert to numbers for proper sorting
      const aNum = typeof aValue === 'string' ? parseFloat(aValue.replace(/[,%$]/g, '')) : aValue;
      const bNum = typeof bValue === 'string' ? parseFloat(bValue.replace(/[,%$]/g, '')) : bValue;
      
      return direction === 'asc' ? aNum - bNum : bNum - aNum;
    });
  };

  // Get product data for the detailed view from actual data
  const getProductDetailData = (groupId, viewType) => {
    console.log('🔍 getProductDetailData called with:', { groupId, viewType });
    console.log('📦 companyAccounts:', data.companyAccounts);
    
    // Try to get real products from the data
    const companyAccounts = data.companyAccounts?.['jungle-group'];
    
    if (companyAccounts) {
      // Search through all seller accounts for the group
      for (const sellerKey in companyAccounts) {
        const seller = companyAccounts[sellerKey];
        console.log(`🔎 Checking seller: ${sellerKey}`, seller);
        
        if (seller.groups) {
          const group = seller.groups.find(g => g.id === groupId);
          console.log(`🎯 Found group for ${groupId}:`, group);
          
          if (group && group.products && (Array.isArray(group.products) ? group.products.length > 0 : ((group.products.parent && group.products.parent.length > 0) || (group.products.child && group.products.child.length > 0)))) {
            const totalCount = Array.isArray(group.products)
              ? group.products.length
              : (group.products.parent?.length || 0) + (group.products.child?.length || 0);
            console.log(`✅ Group has products (${totalCount}):`, group.products);
            
            // Filter products based on viewType
            let filteredProducts = [];
            if (viewType === 'parent') {
              // For parent view, get parent products from the nested structure and sort by name
              filteredProducts = (group.products?.parent || []).slice().sort((a, b) => {
                const an = (a.product || '').toLowerCase();
                const bn = (b.product || '').toLowerCase();
                return an.localeCompare(bn);
              });
            } else if (viewType === 'child') {
              // For child view, get child products from the nested structure and sort by name
              filteredProducts = (group.products?.child || []).slice().sort((a, b) => {
                const an = (a.product || '').toLowerCase();
                const bn = (b.product || '').toLowerCase();
                return an.localeCompare(bn);
              });
            }
            
            console.log(`🔍 Filtered products for ${viewType} view:`, filteredProducts.length, 'products');
            return filteredProducts;
          } else {
            console.log(`⚠️ Group has no products or empty array:`, group?.products);
          }
        }
      }
    }
    
    // Fallback to sample products if no real data found
    const sampleProducts = [
      {
        brand: 'TPS',
        product: 'Lemon Tree',
        custom: 'Garden',
        stats: {
          totalSales: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          unitsSold: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          tacos: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          conversion: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          organicSales: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          adImpressions: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          adCpc: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          adClicks: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          adSales: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          adOrders: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          adSpend: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          adSpendPerUnit: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          totalReferralFees: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          inventory: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          bsr: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          cogs: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          price: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          avgSalePrice: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          totalReferralFeesPercent: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          totalFbaFees: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          totalFees: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          storageFees: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          inboundShippingFees: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          profitPerUnit: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          profitPercent: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 },
          profitTotal: { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0, week6: 0, week7: 0, week8: 0, week9: 0, week10: 0, week11: 0, week12: 0, week13: 0, week14: 0, week15: 0 }
        }
      },
      {
        brand: 'TPS',
        product: 'Hydrangea',
        custom: 'Garden',
        stats: {
          totalSales: { week1: 8925, week2: 9634, week3: 7456, week4: 8213, week5: 9678, week6: 9123, week7: 10456, week8: 8789, week9: 9834, week10: 10567, week11: 11234, week12: 10123, week13: 10890, week14: 11456, week15: 11023 },
          unitsSold: { week1: 698, week2: 845, week3: 723, week4: 867, week5: 934, week6: 876, week7: 1089, week8: 934, week9: 1023, week10: 1156, week11: 1234, week12: 1123, week13: 1189, week14: 1267, week15: 1234 },
          tacos: { week1: 9.87, week2: 10.34, week3: 12.56, week4: 13.21, week5: 11.98, week6: 10.67, week7: 9.45, week8: 11.23, week9: 10.89, week10: 9.76, week11: 9.23, week12: 10.45, week13: 9.87, week14: 8.97, week15: 9.56 },
          conversion: { week1: 7.9, week2: 8.5, week3: 7.2, week4: 8.1, week5: 8.8, week6: 8.3, week7: 9.1, week8: 7.7, week9: 8.6, week10: 9.0, week11: 9.2, week12: 8.7, week13: 8.9, week14: 9.5, week15: 9.1 },
          organicSales: { week1: 62, week2: 54, week3: 58, week4: 51, week5: 47, week6: 59, week7: 64, week8: 52, week9: 56, week10: 61, week11: 63, week12: 57, week13: 59, week14: 65, week15: 62 },
          adImpressions: { week1: 16234, week2: 24567, week3: 18945, week4: 21678, week5: 26234, week6: 23456, week7: 29123, week8: 20567, week9: 27234, week10: 31456, week11: 33234, week12: 29567, week13: 32123, week14: 34567, week15: 32890 },
          adCpc: { week1: 1.98, week2: 1.56, week3: 1.87, week4: 1.72, week5: 1.69, week6: 1.75, week7: 1.53, week8: 1.81, week9: 1.64, week10: 1.48, week11: 1.45, week12: 1.67, week13: 1.59, week14: 1.43, week15: 1.52 },
          adClicks: { week1: 423, week2: 598, week3: 487, week4: 576, week5: 689, week6: 634, week7: 756, week8: 567, week9: 698, week10: 812, week11: 845, week12: 756, week13: 823, week14: 889, week15: 867 },
          adSales: { week1: 2345, week2: 3123, week3: 2067, week4: 2398, week5: 3045, week6: 2789, week7: 3834, week8: 2567, week9: 3234, week10: 4123, week11: 4345, week12: 3890, week13: 4234, week14: 4567, week15: 4398 },
          adOrders: { week1: 189, week2: 267, week3: 198, week4: 234, week5: 289, week6: 256, week7: 334, week8: 245, week9: 289, week10: 367, week11: 389, week12: 345, week13: 378, week14: 412, week15: 398 },
          adSpend: { week1: 834, week2: 967, week3: 912, week4: 1045, week5: 1167, week6: 1089, week7: 1289, week8: 989, week9: 1198, week10: 1367, week11: 1445, week12: 1298, week13: 1389, week14: 1523, week15: 1456 },
          adSpendPerUnit: { week1: 1.19, week2: 1.14, week3: 1.26, week4: 1.20, week5: 1.25, week6: 1.24, week7: 1.18, week8: 1.06, week9: 1.17, week10: 1.18, week11: 1.17, week12: 1.16, week13: 1.17, week14: 1.20, week15: 1.18 },
          totalReferralFees: { week1: 32.45, week2: 28.67, week3: 41.23, week4: 39.56, week5: 35.78, week6: 38.67, week7: 31.89, week8: 43.56, week9: 37.89, week10: 34.23, week11: 38.67, week12: 35.45, week13: 39.87, week14: 42.34, week15: 40.56 },
          inventory: { week1: 1890, week2: 1823, week3: 1756, week4: 1689, week5: 1612, week6: 1534, week7: 1467, week8: 1398, week9: 1321, week10: 1245, week11: 1189, week12: 1123, week13: 1067, week14: 1012, week15: 967 },
          bsr: { week1: 18456, week2: 17234, week3: 19567, week4: 18123, week5: 16890, week6: 15678, week7: 14567, week8: 16234, week9: 15123, week10: 13890, week11: 13234, week12: 14567, week13: 13890, week14: 12567, week15: 13123 },
          cogs: { week1: 0.89, week2: 0.78, week3: 0.69, week4: 0.67, week5: 0.71, week6: 0.73, week7: 0.65, week8: 0.72, week9: 0.68, week10: 0.64, week11: 0.62, week12: 0.69, week13: 0.66, week14: 0.61, week15: 0.65 },
          price: { week1: 15.99, week2: 15.99, week3: 14.99, week4: 14.99, week5: 15.49, week6: 15.49, week7: 14.99, week8: 14.79, week9: 15.29, week10: 14.99, week11: 15.49, week12: 15.99, week13: 15.79, week14: 16.29, week15: 15.89 },
          avgSalePrice: { week1: 12.78, week2: 11.40, week3: 10.31, week4: 9.47, week5: 10.36, week6: 10.41, week7: 9.60, week8: 9.41, week9: 9.61, week10: 9.14, week11: 9.10, week12: 9.01, week13: 9.16, week14: 9.04, week15: 8.93 },
          totalReferralFeesPercent: { week1: 1134.56, week2: 1189.23, week3: 1156.78, week4: 1245.67, week5: 1423.89, week6: 1334.56, week7: 1198.23, week8: 1267.89, week9: 1356.78, week10: 1445.67, week11: 1523.45, week12: 1398.67, week13: 1456.78, week14: 1567.89, week15: 1498.34 },
          totalFbaFees: { week1: 2789.23, week2: 3156.78, week3: 2967.45, week4: 3234.56, week5: 3678.90, week6: 3456.78, week7: 3934.56, week8: 3123.45, week9: 3567.89, week10: 4012.34, week11: 4234.56, week12: 3789.23, week13: 4123.45, week14: 4456.78, week15: 4298.67 },
          totalFees: { week1: 4567.89, week2: 5234.56, week3: 4912.34, week4: 5456.78, week5: 6123.45, week6: 5789.23, week7: 6456.78, week8: 5678.90, week9: 6234.56, week10: 6789.23, week11: 7123.45, week12: 6456.78, week13: 6890.12, week14: 7234.56, week15: 6987.34 },
          storageFees: { week1: 112.34, week2: 134.56, week3: 128.90, week4: 143.67, week5: 167.89, week6: 154.23, week7: 172.45, week8: 134.67, week9: 156.78, week10: 178.90, week11: 189.23, week12: 167.45, week13: 178.90, week14: 194.56, week15: 186.78 },
          inboundShippingFees: { week1: 123.45, week2: 142.67, week3: 119.89, week4: 128.34, week5: 156.78, week6: 143.45, week7: 167.89, week8: 132.56, week9: 149.23, week10: 171.67, week11: 178.90, week12: 156.78, week13: 167.89, week14: 183.45, week15: 175.67 },
          profitPerUnit: { week1: 4.89, week2: 4.12, week3: 1.67, week4: 1.73, week5: 1.98, week6: 2.23, week7: 1.45, week8: 2.67, week9: 2.14, week10: 1.72, week11: 1.78, week12: 1.89, week13: 2.12, week14: 2.45, week15: 2.23 },
          profitPercent: { week1: 38.23, week2: 36.14, week3: 16.19, week4: 18.27, week5: 19.11, week6: 21.41, week7: 15.10, week8: 28.38, week9: 22.26, week10: 18.82, week11: 19.56, week12: 20.98, week13: 23.15, week14: 27.11, week15: 24.98 },
          profitTotal: { week1: 3412, week2: 3478, week3: 1207, week4: 1499, week5: 1849, week6: 1953, week7: 1579, week8: 2493, week9: 2189, week10: 1988, week11: 2198, week12: 2123, week13: 2523, week14: 3104, week15: 2751 }
        }
      }
    ];

    console.log('⚠️ No API products found, returning empty array');
    return [];
  };

  // Render detailed product view
  const renderDetailView = () => {
    const products = getProductDetailData(detailView.groupId, detailView.type);
    
    // Select stats based on current view type and filter by selected metrics
    const allAvailableStats = detailView.type === 'parent' ? parentStats : childStats;
    const allStats = allAvailableStats.filter(stat => detailSelectedMetrics.has(stat.key));

    // First, filter products based on column filters and parent filter
    let filteredProducts = products.filter(product => {
      // Apply parent filter if set (for child view)
      if (parentFilter && detailView.type === 'child') {
        if (product.custom !== parentFilter) {
          return false;
        }
      }
      
      return ['brand', 'product', 'custom'].every(column => {
        if (columnFilters[column].length === 0) return true;
        return columnFilters[column].includes(product[column]);
      });
    });

    // Sort products by pinned stat (or Total Sales default) if sorting by period columns (weeks or months)
    if (detailSortConfig.key && (detailSortConfig.key.startsWith('week') || detailSortConfig.key.startsWith('month'))) {
      filteredProducts = sortProductsByWeeklyStat(filteredProducts, detailSortConfig.key, detailSortConfig.direction);
    }

    // Then, group rows by product
    const groupedRows = {};
    filteredProducts.forEach(product => {
      const productKey = `${product.brand}-${product.product}`;
      groupedRows[productKey] = {
        productInfo: {
          brand: product.brand,
          product: product.product,
          custom: product.custom
        },
        rows: allStats.map(stat => {
          // Use data property instead of stats for parent products
          const productData = product.data || product.stats;
          const week1Value = formatValue(productData[stat.key]?.week1, stat.type);
          
          return {
            brand: product.brand,
            product: product.product,
            custom: product.custom,
            stat: stat.label,
            week1: week1Value,
            rawValues: { week1: productData[stat.key]?.week1 }
          };
        })
      };
    });

    // Sort function for comparing values
    const compareValues = (aValue, bValue) => {
      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      
      // Convert to numbers if possible for numeric sorting
      const aNum = Number(aValue);
      const bNum = Number(bValue);
      
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return detailSortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }
      
      // String sorting
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      return detailSortConfig.direction === 'asc' ? 
        aStr.localeCompare(bStr) : 
        bStr.localeCompare(aStr);
    };

    // Get default stat order (Total Sales first, then rest alphabetically)
    const getDefaultStatOrder = () => {
      const defaultFirst = ['Total Sales'];
      const remaining = allStats
        .filter(stat => !defaultFirst.includes(stat.label))
        .map(stat => stat.label)
        .sort();
      return [...defaultFirst, ...remaining];
    };

    // Sort rows within each product group, handling pinned stat and custom order
    Object.values(groupedRows).forEach(group => {
      group.rows.sort((a, b) => {
        // First, check if there's a pinned stat
        if (pinnedStat) {
          const aIsPinned = a.stat === pinnedStat;
          const bIsPinned = b.stat === pinnedStat;
          
          if (aIsPinned && !bIsPinned) return -1;
          if (!aIsPinned && bIsPinned) return 1;
          
          // If both are pinned (shouldn't happen with single pinned stat)
          if (aIsPinned && bIsPinned) return 0;
        }
        
        // For non-pinned stats, use custom order if available, otherwise default order
        const orderToUse = statOrder.length > 0 ? statOrder : getDefaultStatOrder();
        const aIndex = orderToUse.indexOf(a.stat);
        const bIndex = orderToUse.indexOf(b.stat);
        
        // If both stats are in the order array, use that order
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        
        // If only one is in the order array, prioritize it
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        
        // If neither is in the order array, fall back to alphabetical
        return a.stat.localeCompare(b.stat);
      });
    });

    // Convert grouped rows back to flat array with product headers
    const tableRows = [];
    Object.values(groupedRows).forEach((group, groupIndex) => {
      // Add product header
      tableRows.push({
        isProductHeader: true,
        ...group.productInfo
      });
      
      // Add sorted rows for this product
      tableRows.push(...group.rows);
      
      // Add spacer after each group except the last one
      if (groupIndex < Object.values(groupedRows).length - 1) {
        tableRows.push({ isSpacer: true });
      }
    });

    return (
      <div className="space-y-6">
        {/* Header with back button and table */}
        <div className="bg-white rounded-2xl shadow-soft border border-secondary-200 overflow-hidden animate-fade-in">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleBackToGroups}
                    className="flex items-center space-x-1.5 text-primary-600 hover:text-primary-800 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span className="text-xs font-medium">Back to Groups</span>
                  </button>
                </div>
                <h2 className="text-lg font-bold text-secondary-900 mt-1">
                  {detailView.type === 'parent' ? 'Parent View' : 'Child View'}: {detailView.groupName}
                </h2>
                <div className="flex items-center space-x-3">
                  <p className="text-xs text-secondary-600">from {detailView.companyName}</p>
                  {parentFilter && detailView.type === 'child' && (
                    <div className="flex items-center space-x-1.5 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                      <span className="font-medium">Filtered: {parentFilter}</span>
                      <button
                        onClick={() => setParentFilter(null)}
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                        title="Clear parent filter"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                  {pinnedStat ? (
                    <div className="flex items-center space-x-1.5 px-2 py-0.5 bg-primary-100 text-primary-800 rounded-full text-xs">
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 4l-8 8h6v8h4v-8h6z" />
                      </svg>
                      <span className="font-medium">Pinned: {pinnedStat}</span>
                      <button
                        onClick={() => setPinnedStat(null)}
                        className="text-primary-600 hover:text-primary-800 transition-colors"
                        title="Unpin stat"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1.5 px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-full text-xs">
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                        <path d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z" />
                      </svg>
                      <span className="font-medium">Weekly Sort: Total Sales</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Navigation between Parent and Child */}
              <div className="flex items-center space-x-2">
                {detailView.type === 'parent' ? (
                  <button
                    onClick={() => {
                      const newDetailView = { ...detailView, type: 'child' };
                      setDetailView(newDetailView);
                      setParentFilter(null); // Clear parent filter when manually switching to child
                      setColumnFilters({ brand: [], product: [], custom: [] }); // Reset column filters
                      setDetailSortConfig({ key: null, direction: 'asc' }); // Reset sorting
                      setCollapsedProducts(new Set()); // Reset collapsed state
                      if (onDetailViewChange) {
                        onDetailViewChange(newDetailView);
                      }
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-secondary-500 hover:bg-secondary-600 rounded-md shadow-sm transition-colors border border-secondary-600 min-w-[60px]"
                  >
                    Child
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      const newDetailView = { ...detailView, type: 'parent' };
                      setDetailView(newDetailView);
                      setParentFilter(null); // Clear parent filter when switching to parent
                      setColumnFilters({ brand: [], product: [], custom: [] }); // Reset column filters
                      setDetailSortConfig({ key: null, direction: 'asc' }); // Reset sorting
                      setCollapsedProducts(new Set()); // Reset collapsed state
                      if (onDetailViewChange) {
                        onDetailViewChange(newDetailView);
                      }
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-md shadow-sm transition-colors border border-primary-600 min-w-[60px]"
                  >
                    Parent
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="relative">
            <div ref={detailViewTableRef} className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-180px)]">
              <table className="border-collapse w-full" style={{ minWidth: '1050px' }}>
                <thead className="bg-gradient-to-r from-secondary-50 to-secondary-100 sticky top-0 z-50">
                  <tr>
                    {['brand', 'product', 'custom'].map((column, colIndex) => (
                      <th 
                        key={column}
                        className={`px-3 py-2 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200 sticky z-20 bg-gradient-to-r from-secondary-50 to-secondary-100`}
                        style={
                          colIndex === 0 ? { left: '0', width: '12%', minWidth: '120px', position: 'sticky' } :
                          colIndex === 1 ? { left: '12%', width: '30%', minWidth: '250px', position: 'sticky' } :
                          colIndex === 2 ? { left: '42%', width: '18%', minWidth: '180px', position: 'sticky' } :
                          { width: '25%', minWidth: '200px' }
                        }
                      >
                        <div className="flex items-center justify-center space-x-2">
                          <div className="flex items-center space-x-1">
                            <span>{column.toUpperCase()}</span>
                            {detailSortConfig.key === column && (
                              <svg className={`w-4 h-4 ${detailSortConfig.direction === 'asc' ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                            )}
                            {columnFilters[column].length > 0 && (
                              <span className="text-xs text-primary-600">
                                ({columnFilters[column].length})
                              </span>
                            )}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              const isCurrentlyActive = activeDropdown?.type === 'column' && activeDropdown?.data?.column === column;
                              
                              if (isCurrentlyActive) {
                                setActiveDropdown(null);
                              } else {
                                setActiveDropdown({
                                  type: 'column',
                                  data: { column },
                                  position: {
                                    top: rect.bottom + 4,
                                    left: rect.left
                                  }
                                });
                              }
                            }}
                            className="text-secondary-400 hover:text-secondary-600 transition-colors"
                          >
                            <svg className="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor">
                              <path d="M480 64H32c-17.7 0-27.6 20.5-16.1 34.3L192 288v146c0 14.8 12 26.8 26.8 26.8h74.4c14.8 0 26.8-12 26.8-26.8V288l176.1-189.7C507.6 84.5 497.7 64 480 64z" />
                            </svg>
                          </button>
                        </div>
                        
                        {activeDropdown?.type === 'column' && activeDropdown?.data?.column === column && (
                          <ColumnFilterDropdown
                            ref={activeDropdownRef}
                            column={column}
                            uniqueValues={Array.from(new Set(products.map(p => p[column])))}
                            selectedFilters={columnFilters[column]}
                            onFilterChange={(newFilters) => {
                              setColumnFilters(prev => ({
                                ...prev,
                                [column]: newFilters
                              }));
                            }}
                            onSortAsc={() => {
                              handleDetailSort(column);
                              setDetailSortConfig({ key: column, direction: 'asc' });
                            }}
                            onSortDesc={() => {
                              handleDetailSort(column);
                              setDetailSortConfig({ key: column, direction: 'desc' });
                            }}
                            onClose={() => setActiveDropdown(null)}
                            position={activeDropdown.position}
                          />
                        )}
                      </th>
                    ))}
                    <th className="px-3 py-2 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b sticky z-20 bg-gradient-to-r from-secondary-50 to-secondary-100 shadow-[2px_0_8px_rgba(0,0,0,0.1)]" style={{ left: '60%', width: '25%', minWidth: '200px', position: 'sticky' }}>
                      <div className="flex items-center justify-center space-x-2">
                        <div 
                          className="flex items-center space-x-1 cursor-pointer hover:bg-secondary-100 transition-colors rounded px-1 py-0.5"
                          onClick={() => handleDetailSort('stat')}
                        >
                          <span>STAT</span>
                          {detailSortConfig.key === 'stat' && (
                            <svg className={`w-4 h-4 ${detailSortConfig.direction === 'asc' ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                          )}
                        </div>
                        
                        <div className="flex items-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              const isCurrentlyActive = activeDropdown?.type === 'stat';
                              
                              if (isCurrentlyActive) {
                                setActiveDropdown(null);
                              } else {
                                setActiveDropdown({
                                  type: 'stat',
                                  data: {},
                                  position: {
                                    top: rect.bottom + 4,
                                    left: rect.right - 320 // Position to align right edge of dropdown with button
                                  }
                                });
                              }
                            }}
                            className="flex items-center text-secondary-400 hover:text-secondary-600 transition-colors p-1 rounded"
                          >
                            <svg className="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor">
                              <path d="M480 64H32c-17.7 0-27.6 20.5-16.1 34.3L192 288v146c0 14.8 12 26.8 26.8 26.8h74.4c14.8 0 26.8-12 26.8-26.8V288l176.1-189.7C507.6 84.5 497.7 64 480 64z" />
                            </svg>
                            <span className="text-xs ml-1">({detailSelectedMetrics.size})</span>
                          </button>
                          
                          {activeDropdown?.type === 'stat' && (
                            <StatDropdown
                              ref={activeDropdownRef}
                              position={activeDropdown.position}
                            />
                          )}
                        </div>
                      </div>
                    </th>
              {/* Single data column for Sept 7-13, 2025 */}
              <th 
                className="px-3 py-2 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200 cursor-pointer hover:bg-secondary-100 transition-colors"
                style={{ width: '15%', minWidth: '150px' }}
                onClick={() => handleDetailSort('week1')}
                title={pinnedStat ? `Sort by ${pinnedStat} for Sept 7-13, 2025` : `Sort by Total Sales for Sept 7-13, 2025`}
              >
                <div className="flex items-center justify-center space-x-1">
                  <span>SEPT 7-13, 2025</span>
                  {detailSortConfig.key === 'week1' && (
                    <svg className={`w-4 h-4 ${detailSortConfig.direction === 'asc' ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  )}
                  {pinnedStat && (
                    <div className="w-1.5 h-1.5 bg-primary-500 rounded-full"></div>
                  )}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-secondary-100">
                {(() => {
                  let currentProductKey = null;
                  let isCurrentProductCollapsed = false;
                  
                  return tableRows.map((row, index) => {
                  if (row.isSpacer) {
                    return (
                      <tr key={`spacer-${index}`}>
                          <td colSpan={4} className="h-4 bg-secondary-50 sticky z-10 shadow-[2px_0_8px_rgba(0,0,0,0.1)]" style={{ left: '0' }}></td>
                          <td colSpan={1} className="h-4 bg-secondary-50"></td>
                      </tr>
                    );
                  }

                  if (row.isProductHeader) {
                      const productKey = `${row.brand}-${row.product}-${row.custom}`;
                      const isCollapsed = collapsedProducts.has(productKey);
                      
                      // Update tracking variables
                      currentProductKey = productKey;
                      isCurrentProductCollapsed = isCollapsed;
                      
                    return (
                      <tr 
                        key={`header-${index}`}
                        className="bg-blue-100 border-y border-blue-200"
                      >
                        {/* Fixed product info section */}
                        <td colSpan={4} className="px-3 py-2 sticky z-20 bg-blue-100 shadow-[2px_0_8px_rgba(0,0,0,0.1)]" style={{ left: '0px', minWidth: '540px', maxWidth: '540px' }}>
                          <div className="flex items-center space-x-2 overflow-hidden">
                            <svg 
                              onClick={() => toggleProduct(productKey)}
                              className={`w-5 h-5 text-blue-700 transition-transform duration-200 flex-shrink-0 cursor-pointer hover:text-blue-900 ${isCollapsed ? '-rotate-90' : ''}`} 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                            <div className="w-7 h-7 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                              <span className="text-white font-bold text-xs">
                                {row.product.charAt(0)}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0 overflow-hidden">
                              <h4 className="text-sm font-semibold text-blue-900 truncate" title={row.product}>{row.product}</h4>
                              <p className="text-xs text-blue-700 truncate" title={`${row.brand} • ${row.custom}`}>{row.brand} • {row.custom}</p>
                            </div>
                            {detailView.type === 'parent' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewParentChildren(row.product, detailView.groupId, detailView.groupName);
                                }}
                                className="flex items-center space-x-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium transition-colors flex-shrink-0"
                                title="View children of this parent"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <span>Children</span>
                              </button>
                            )}
                          </div>
                        </td>
                        {/* Single data column */}
                        <td colSpan={1} className="px-3 py-2 bg-blue-100" style={{ width: '15%', minWidth: '150px' }}>
                          {/* Empty space for data column */}
                        </td>
                      </tr>
                    );
                  }

                  // Skip stat rows if the current product is collapsed
                  if (isCurrentProductCollapsed) {
                    return null;
                  }

                  return (
                                          <tr 
                        key={`row-${index}`} 
                        draggable={true}
                        onDragStart={(e) => {
                          console.log('Drag start:', row.stat);
                          setDraggedStat(row.stat);
                          e.dataTransfer.effectAllowed = 'move';
                          e.dataTransfer.setData('text/plain', row.stat);
                          // Add some visual feedback
                          e.currentTarget.style.opacity = '0.5';
                        }}
                        onDragOver={(e) => {
                          if (draggedStat && draggedStat !== row.stat) {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            
                            // Calculate if we're in the top or bottom half of the row
                            const rect = e.currentTarget.getBoundingClientRect();
                            const midY = rect.top + rect.height / 2;
                            const position = e.clientY < midY ? 'above' : 'below';
                            
                            setDragOverStat(row.stat);
                            setDragPosition(position);
                          }
                        }}
                        onDragEnter={(e) => {
                          if (draggedStat && draggedStat !== row.stat) {
                            e.preventDefault();
                            
                            // Calculate if we're in the top or bottom half of the row
                            const rect = e.currentTarget.getBoundingClientRect();
                            const midY = rect.top + rect.height / 2;
                            const position = e.clientY < midY ? 'above' : 'below';
                            
                            setDragOverStat(row.stat);
                            setDragPosition(position);
                          }
                        }}
                        onDragLeave={(e) => {
                          // Only clear if we're leaving the row entirely
                          if (!e.currentTarget.contains(e.relatedTarget)) {
                            setDragOverStat(null);
                            setDragPosition(null);
                          }
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          
                          console.log('Drop event:', { draggedStat, targetStat: row.stat, position: dragPosition, statOrder });
                          
                          if (draggedStat && draggedStat !== row.stat) {
                            // Get current order or create from all stats
                            const currentOrder = statOrder.length > 0 ? [...statOrder] : 
                              allStats.map(stat => stat.label);
                            
                            // Remove dragged stat from current position
                            const newOrder = currentOrder.filter(stat => stat !== draggedStat);
                            
                            // Find target index
                            const targetIndex = newOrder.indexOf(row.stat);
                            
                            if (targetIndex !== -1) {
                              // Insert based on position (above or below)
                              const insertIndex = dragPosition === 'above' ? targetIndex : targetIndex + 1;
                              newOrder.splice(insertIndex, 0, draggedStat);
                            } else {
                              // If target not found, add to end
                              newOrder.push(draggedStat);
                            }
                            
                            setStatOrder(newOrder);
                            console.log('New order:', newOrder);
                          }
                          setDraggedStat(null);
                          setDragOverStat(null);
                          setDragPosition(null);
                        }}
                        onDragEnd={(e) => {
                          console.log('Drag end');
                          setDraggedStat(null);
                          setDragOverStat(null);
                          setDragPosition(null);
                          // Reset visual feedback
                          e.currentTarget.style.opacity = '';
                        }}
                        className={`
                          group transition-all duration-200 cursor-move
                          ${pinnedStat === row.stat ? 'bg-primary-50 hover:bg-primary-100' : 'bg-white hover:bg-secondary-50'}
                          ${draggedStat === row.stat ? 'opacity-50 bg-primary-50' : ''}
                          ${dragOverStat === row.stat && dragPosition === 'above' ? 'border-t-4 border-blue-500' : ''}
                          ${dragOverStat === row.stat && dragPosition === 'below' ? 'border-b-4 border-blue-500' : ''}
                          relative
                        `}
                      >
                      <td className={`px-3 py-1.5 text-sm text-secondary-900 font-medium sticky z-10 ${pinnedStat === row.stat ? 'bg-primary-50 group-hover:bg-primary-100' : 'bg-white group-hover:bg-secondary-50'}`} style={{ left: '0', width: '12%', minWidth: '120px' }}>
                        <div className="truncate" title={row.brand}>{row.brand}</div>
                      </td>
                      <td className={`px-3 py-1.5 text-sm text-secondary-900 font-medium sticky z-10 ${pinnedStat === row.stat ? 'bg-primary-50 group-hover:bg-primary-100' : 'bg-white group-hover:bg-secondary-50'}`} style={{ left: '12%', width: '30%', minWidth: '250px' }}>
                        <div className="truncate" title={row.product}>{row.product}</div>
                      </td>
                      <td className={`px-3 py-1.5 text-sm text-secondary-700 sticky z-10 ${pinnedStat === row.stat ? 'bg-primary-50 group-hover:bg-primary-100' : 'bg-white group-hover:bg-secondary-50'}`} style={{ left: '42%', width: '18%', minWidth: '180px' }}>
                        <div className="truncate" title={row.custom}>{row.custom}</div>
                      </td>
                      <td 
                        className={`px-3 py-1.5 text-sm text-secondary-900 font-medium relative sticky z-10 shadow-[2px_0_8px_rgba(0,0,0,0.1)] ${pinnedStat === row.stat ? 'bg-primary-50 group-hover:bg-primary-100' : 'bg-white group-hover:bg-secondary-50'}`}
                        style={{ left: '60%', width: '25%', minWidth: '200px' }}
                        onMouseEnter={() => setHoveredStat(row.stat)}
                        onMouseLeave={() => setHoveredStat(null)}
                      >
                        <div className="flex items-center space-x-1.5">
                                                      <button
                              onClick={() => {
                                // If this stat is already pinned, unpin it
                                if (pinnedStat === row.stat) {
                                  setPinnedStat(null);
                                } else {
                                  // Pin this stat (replaces any previously pinned stat)
                                  setPinnedStat(row.stat);
                                }
                              }}
                              className={`
                                ${(hoveredStat === row.stat || pinnedStat === row.stat) ? 'opacity-100' : 'opacity-0'}
                                group-hover:opacity-100 transition-opacity duration-200
                                ${pinnedStat === row.stat ? 'text-primary-600' : 'text-secondary-400 hover:text-secondary-600'}
                              `}
                              title={pinnedStat === row.stat ? 'Unpin stat' : 'Pin stat to top'}
                            >
                              <svg 
                                className={`w-3.5 h-3.5 transform transition-transform duration-200 ${pinnedStat === row.stat ? 'rotate-180' : ''}`} 
                                fill="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path d="M12 4l-8 8h6v8h4v-8h6z" />
                              </svg>
                            </button>
                          <span>
                            {row.stat}
                          </span>
                        </div>
                      </td>
                      {/* Single data cell for Sept 7-13, 2025 */}
                      <td className="px-3 py-1.5 text-sm text-center" style={{ width: '15%', minWidth: '150px' }}>
                        <span className={getMetricColor(row.stat, row.week1)}>{row.week1}</span>
                      </td>
                    </tr>
                  );
                  });
                })()}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Sample compare data (current year vs last year)
  const getCompareData = () => {
    return [
      {
        id: 'current-year',
        label: 'Current Year (2024)',
        jan: '$0',
        feb: '$0',
        mar: '$0',
        apr: '$0',
        may: '$0',
        jun: '$0',
        jul: '$0',
        aug: '$0',
        sep: '$0',
        oct: '$0'
      },
      {
        id: 'last-year',
        label: 'Last Year (2023)', 
        jan: '$0',
        feb: '$0',
        mar: '$0',
        apr: '$0',
        may: '$0',
        jun: '$0',
        jul: '$0',
        aug: '$0',
        sep: '$0',
        oct: '$0'
      },
      {
        id: 'change',
        label: 'Change (%)',
        jan: '0%',
        feb: '0%',
        mar: '0%',
        apr: '0%',
        may: '0%',
        jun: '0%',
        jul: '0%',
        aug: '0%',
        sep: '0%',
        oct: '0%'
      }
    ];
  };

  // Render compare section
  const renderCompareSection = () => {
    const compareData = getCompareData();
    
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-secondary-50 to-secondary-100">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                Compare Button (Compare to last year)
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>Jan 2024</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>Jan 2023</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>Feb 2024</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>Feb 2023</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>Mar 2024</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>Mar 2023</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>Apr 2024</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>Apr 2023</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>May 2024</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>May 2023</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>Jun 2024</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>Jun 2023</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>Jul 2024</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>Jul 2023</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>Aug 2024</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>Aug 2023</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>Sep 2024</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>Sep 2023</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>Oct 2024</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-700 uppercase tracking-wider border-b border-secondary-200">
                <div className="flex items-center justify-center space-x-1">
                  <span>Oct 2023</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-secondary-100">
            {compareData.map((row, index) => (
                    <tr 
                key={row.id} 
                      className={`group hover:bg-secondary-50 transition-all duration-200 ${
                        index % 2 === 0 ? 'bg-white' : 'bg-secondary-25'
                      }`}
                    >
                <td className="px-6 py-3 text-sm text-secondary-900 font-medium">{row.label}</td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.jan}</span>
                </td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.jan}</span>
                </td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.feb}</span>
                </td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.feb}</span>
                </td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.mar}</span>
                </td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.mar}</span>
                </td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.apr}</span>
                </td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.apr}</span>
                </td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.may}</span>
                </td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.may}</span>
                </td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.jun}</span>
                </td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.jun}</span>
                </td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.jul}</span>
                </td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.jul}</span>
                </td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.aug}</span>
                </td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.aug}</span>
                </td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.sep}</span>
                </td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.sep}</span>
                </td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.oct}</span>
                </td>
                <td className="px-6 py-3 text-sm text-center">
                  <span className="text-gray-900 font-medium">{row.oct}</span>
                </td>
                    </tr>
                  ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Early return if data is not available
  if (!data || (!data.companies && !data.parentCompanies && !data.companyAccounts)) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-soft dark:shadow-slate-900/30 border border-secondary-200 dark:border-slate-700 p-12 text-center animate-fade-in transition-colors duration-300">
        <div className="w-16 h-16 bg-secondary-100 dark:bg-slate-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">📊</span>
        </div>
        <h3 className="text-lg font-semibold text-secondary-900 dark:text-slate-100 mb-2">No Data Available</h3>
        <p className="text-secondary-600 dark:text-slate-400">Data is currently not available for this organization.</p>
      </div>
    );
  }

  const parentCompanyData = getParentCompanyData();

    return (
    <div className="space-y-6">
      {detailView ? (
        renderDetailView()
      ) : (
        <>
          {/* Parent Company Table */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-soft dark:shadow-slate-900/30 border border-secondary-200 dark:border-slate-700 overflow-hidden animate-fade-in transition-colors duration-300">
            {renderTableHeader('The Jungle Group, INC', '🏢', 'parent-company')}
            {renderTable(parentCompanyData, 'parent-company', collapsedTables.has('parent-company'))}
          </div>

          {/* Company/Seller Account Tables */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-soft dark:shadow-slate-900/30 border border-secondary-200 dark:border-slate-700 overflow-hidden animate-fade-in transition-colors duration-300">
            {renderTableHeader('Company / Seller Account', '🏬', 'companies')}
            {!collapsedTables.has('companies') && renderCompanies()}
          </div>

          {/* Groups Table */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-soft dark:shadow-slate-900/30 border border-secondary-200 dark:border-slate-700 animate-fade-in transition-colors duration-300">
            {renderTableHeader('Groups', '👥', 'groups')}
            {!collapsedTables.has('groups') && renderGroups()}
          </div>

          {/* Compare Section */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-soft dark:shadow-slate-900/30 border border-secondary-200 dark:border-slate-700 overflow-hidden animate-fade-in transition-colors duration-300">
            {renderTableHeader('Compare (Year over Year)', '📊', 'compare')}
            {!collapsedTables.has('compare') && renderCompareSection()}
          </div>
        </>
      )}
    </div>
  );
};

export default DataTable; 